/**
 * Football match engine. Pure TS, deterministic from MatchConfig.seed.
 *
 * Conventions:
 *  - World coords per core/contracts (x along length, z across, y up, metres).
 *  - PlayerState.heading (per contracts): facing direction = (cos h, 0, sin h),
 *    i.e. heading = atan2(vz, vx). BallState.pos.y is the ball centre (BR on grass).
 *  - Each team works in its own "attacking frame": fx = x * dir, fz = z * dir, so the
 *    goal it attacks is always at fx = +52.5. dir flips at half-time.
 *  - Shot outcomes are decided at the moment of the strike (ground truth for the
 *    director), then the ball/keeper are driven to physically act it out.
 */
import type {
  GraphicKind, MatchConfig, MatchEvent, MatchEventType, MatchPhase, MatchSim, MatchStats, PlayerAnim,
  PlayerProfile, PlayerState, ShotSize, TeamMatchStats, TeamProfile, TeamSide, Vec3, WorldSnapshot,
} from '../core/contracts';
import { createRng } from '../core/rng';
import { clamp, damp } from '../core/math';
import { FORMATIONS, type SlotDef } from './formations';
import { line, type Line, type TextVars } from './commentary';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HL = 52.5, HW = 34, GHW = 3.66, GH = 2.44, BR = 0.11, G = 9.81;
const BOX_X = HL - 16.5;          // frame x of the penalty-box edge
const BOX_HW = 20.16;
const POST_R = 0.06;
const CLOCK_HALF = 2700;
const BENCH_Z = -38;
/** Players' tunnel mouth (near side, behind the halfway line). */
const TUNNEL_Z = -36.5;
const PRED_N = 36, PRED_DT = 0.1;
/** Real seconds the teams spend walking off at half-time before ends are swapped. */
const HT_WALK = 11;
/** Global open-play pace multiplier for player movement. */
const TEMPO = 1.22;

type ShotKind = 'open' | 'header' | 'volley' | 'fk' | 'pen';
type ShotOutcome = 'goal' | 'catch' | 'parry' | 'tip' | 'near' | 'wide' | 'post' | 'bar' | 'block' | 'line';
type FlightKind = 'pass' | 'through' | 'long' | 'cross' | 'cutback' | 'clear' | 'throw' | 'goal_kick' | 'kickoff' | 'shot' | 'loose' | 'keeper';
type RestartKind = 'throw_in' | 'corner' | 'goal_kick' | 'free_kick' | 'penalty' | 'kickoff';

interface TeamRt {
  side: TeamSide;
  prof: TeamProfile;
  dir: 1 | -1;
  players: SP[];
  other: TeamRt;
  benchLeft: PlayerProfile[];
  subPlan: number[];
  shots: number; onTarget: number; corners: number; fouls: number; yellow: number; red: number;
  passes: number; passesOk: number; xg: number; possT: number;
  dugoutX: number;
}

interface SP {
  i: number;
  st: PlayerState;
  prof: PlayerProfile;
  team: TeamRt;
  slot: SlotDef;
  isGK: boolean;
  pace: number; passing: number; shooting: number; tackling: number; dribbling: number; keeping: number; heading: number; aggression: number;
  tx: number; tz: number; want: number; direct: boolean;
  animLock: number; animLen: number; lockedAnim: PlayerAnim;
  stun: number;
  decideT: number;
  pending: Action | null; windup: number;
  supT: number; sx: number; sz: number;
  runT: number;
  dribX: number; dribZ: number; holding: boolean; carryT: number;
  tackleCd: number;
  yellow: number; off: boolean;
  /** Being substituted: walking to the halfway touchline. */
  subOff: boolean;
  /** Peak root height of the current dive (keepers). */
  diveY: number;
  stamina: number;
  ignoreBall: number;
  attempted: number;
  ix: number; iz: number; itime: number;
  sepX: number; sepZ: number;
  touches: number; passes: number; shots: number; dist: number; topSpeed: number;
}

interface Action {
  kind: 'pass' | 'through' | 'long' | 'cross' | 'cutback' | 'shot' | 'clear' | 'throw' | 'goal_kick' | 'kickoff' | 'keeper_throw' | 'keeper_punt';
  to: SP | null;
  x: number; z: number;
  shotKind?: ShotKind;
}

interface ShotPlan {
  shooter: SP; kind: ShotKind; outcome: ShotOutcome;
  keeper: SP | null; blocker: SP | null;
  /** Defender who clears it off the line ('line' outcome). */
  liner: SP | null;
  goalX: number; kx: number; kz: number;  // keeper save point (world)
  ty: number;                              // planned ball height at the goal plane
  xg: number; dist: number; resolved: boolean;
}

interface Flight {
  id: number; kind: FlightKind; from: SP; intended: SP | null; t0: number;
  offside: boolean; shot: ShotPlan | null;
}

interface Restart {
  kind: RestartKind; team: TeamRt; x: number; z: number; direct: boolean;
  taker: SP | null;
}

interface Scheduled { t: number; fn: () => void; tag?: string }

// ---------------------------------------------------------------------------
// Event defaults
// ---------------------------------------------------------------------------

type Subj = 'player' | 'second' | 'ball' | 'none';
const EV: Record<MatchEventType, [number, number, ShotSize[], Subj, boolean, GraphicKind[]?]> = {
  kickoff: [0.25, 3, ['wide', 'extreme_wide'], 'ball', false],
  pass: [0.08, 2, ['wide'], 'ball', false],
  long_ball: [0.1, 2.5, ['wide', 'extreme_wide'], 'ball', false],
  cross: [0.3, 2, ['wide'], 'ball', false],
  dribble: [0.22, 2, ['medium', 'wide'], 'player', false],
  tackle: [0.25, 2, ['wide', 'medium'], 'player', false],
  interception: [0.12, 2, ['wide'], 'ball', false],
  shot: [0.5, 2.5, ['wide', 'medium'], 'ball', false],
  shot_on_target: [0.55, 2.5, ['wide', 'medium'], 'ball', false],
  save: [0.68, 3, ['wide', 'medium'], 'player', true],
  goal: [1.0, 10, ['close', 'medium'], 'player', true, ['goal_lower_third']],
  near_miss: [0.62, 3, ['wide', 'medium'], 'ball', true],
  woodwork: [0.75, 3, ['wide', 'medium'], 'ball', true],
  foul: [0.3, 3, ['medium', 'wide'], 'player', false],
  yellow_card: [0.55, 5, ['close', 'medium'], 'player', false, ['card_lower_third']],
  red_card: [0.85, 6, ['close', 'medium'], 'player', true, ['card_lower_third']],
  offside: [0.2, 2.5, ['wide'], 'player', false],
  corner: [0.35, 5, ['wide', 'medium'], 'ball', false],
  throw_in: [0.08, 2, ['wide'], 'player', false],
  goal_kick: [0.08, 3, ['wide', 'extreme_wide'], 'ball', false],
  free_kick: [0.2, 4, ['wide', 'medium'], 'ball', false],
  penalty: [0.9, 8, ['medium', 'close'], 'player', true],
  substitution: [0.3, 6, ['medium', 'close'], 'player', false, ['sub_lower_third']],
  injury: [0.3, 5, ['medium', 'close'], 'player', false],
  celebration: [0.7, 7, ['close', 'medium'], 'player', false],
  manager_reaction: [0.45, 3, ['close', 'medium'], 'none', false],
  crowd_moment: [0.35, 3, ['wide', 'medium'], 'none', false],
  half_time: [0.6, 5, ['wide', 'extreme_wide'], 'none', false, ['half_time_score']],
  full_time: [0.8, 8, ['wide', 'extreme_wide'], 'none', false, ['full_time_score']],
  counter_attack: [0.5, 5, ['wide'], 'ball', false],
  var_check: [0.6, 8, ['medium', 'close'], 'none', false, ['var_check']],
};

interface EmitOpts {
  team?: TeamSide | null; player?: SP | null; second?: SP | null; pos?: Vec3;
  importance?: number; window?: number; sizes?: ShotSize[]; subject?: string | 'ball' | null;
  replay?: boolean; graphics?: GraphicKind[]; text: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const surname = (p: SP | PlayerProfile): string => {
  const prof = 'prof' in p ? p.prof : p;
  const i = prof.name.indexOf(' ');
  return i >= 0 ? prof.name.slice(i + 1) : prof.name;
};

/** Simple xG model in the attacking frame. */
export function xgModel(fx: number, fz: number): number {
  const dx = Math.max(0.5, HL - fx);
  const d = Math.hypot(dx, fz);
  const a = Math.atan2(2 * GHW * dx, dx * dx + fz * fz - GHW * GHW);
  const ang = a < 0 ? a + Math.PI : a;
  return 1 / (1 + Math.exp(-(-1.1 + 1.6 * ang - 0.1 * d)));
}

/** Positional threat 0..1 in the attacking frame. */
function threat(fx: number, fz: number): number {
  const g = clamp((fx + HL) / (2 * HL), 0, 1);
  let v = Math.pow(g, 2.2) * 0.55 * (1 - Math.min(Math.abs(fz) / HW, 1) * 0.35);
  if (fx > BOX_X && Math.abs(fz) < BOX_HW) v += 0.22 * (1 - Math.abs(fz) / BOX_HW) + 0.12 * (fx - BOX_X) / 16.5;
  return clamp(v, 0, 1);
}

const wrapAngle = (a: number): number => {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
};

// ---------------------------------------------------------------------------
// Sim
// ---------------------------------------------------------------------------

class FootballSimImpl implements MatchSim {
  readonly config: MatchConfig;
  private rng; private trng;
  private drama: number;
  private home: TeamRt; private away: TeamRt;
  private all: SP[] = [];
  private everPlayed = new Map<string, SP>();

  private t = 0;
  private phase: MatchPhase = 'pre_match';
  private phaseT = 0;
  private half: 1 | 2 = 1;
  private halfStartT = -1;
  private clock = 0;
  private clockFrozen = false;
  private added = [0, 0];
  private addedSet = false;
  private kickoffFirst: TeamRt;
  private kickoffTeam: TeamRt;
  private score = { home: 0, away: 0 };
  private _events: MatchEvent[] = [];
  private stepEvents: MatchEvent[] = [];
  private nextEventId = 1;
  private scheduled: Scheduled[] = [];
  private _finished = false;

  // ball
  private bp: Vec3 = { x: 0, y: BR, z: 0 };
  private bv: Vec3 = { x: 0, y: 0, z: 0 };
  private spin = 0;
  private owner: SP | null = null;
  private lastTouch: SP | null = null;
  private flight: Flight | null = null;
  private flightSeq = 0;
  private inNet = false;
  private possSide: TeamSide = 'home';
  private keeperHoldT = 0;
  private ballPinned = false;
  private prevBall: Vec3 = { x: 0, y: BR, z: 0 };
  private ballMove: { fx: number; fz: number; fy: number; tx: number; tz: number; t0: number; dur: number } | null = null;

  // phase data
  private restart: Restart | null = null;
  private stopDur = 0;
  private setDur = 0;
  private celeb: { scorer: SP; team: TeamRt; cx: number; cz: number; slots: Map<SP, number> } | null = null;
  private lastPasser: { p: SP; t: number } | null = null;
  private turnoverT = -99;
  private turnoverFx = 0;
  private counterUntil = -1;
  private counterEmitted = true;
  private pressers: Record<TeamSide, SP | null> = { home: null, away: null };
  private offLine: Record<TeamSide, number> = { home: 0, away: 0 };
  /** Defensive line cap (team frame) when defending deep; -99 = none. */
  private defLine: Record<TeamSide, number> = { home: -99, away: -99 };
  private lastRestartT = 0;
  private stoppageGaps: number[] = [];
  private stoppages = 0;
  private cynical = false;
  private shotDists: number[] = [];
  private woodCd = 0;
  private woodHit: 'post' | 'bar' | null = null;
  private pressT: Record<TeamSide, number> = { home: 0, away: 0 };
  private lastCrowdT = 0;
  private lastChanceT = -99;
  /** Patient build-up spell (keep-ball, few fouls) — gives directors 30-50s of tension between stoppages. */
  private calm: { team: TeamRt; until: number } | null = null;
  /** After a parry / block / woodwork the attackers get a sniff at the rebound (box scrambles). */
  private scramble: { team: TeamRt; until: number } | null = null;
  private subQueue: { team: TeamRt; out: SP; inProf: PlayerProfile; t0: number }[] = [];

  // officials
  private ref: Vec3 = { x: 0, y: 0, z: -6 };
  private refV: Vec3 = { x: 0, y: 0, z: 0 };
  private ar1: Vec3 = { x: 20, y: 0, z: -HW - 1 };
  private ar2: Vec3 = { x: -20, y: 0, z: HW + 1 };

  private intensity = 0.1;
  private focus: Vec3 = { x: 0, y: 0, z: 0 };
  private predX = new Float64Array(PRED_N);
  private predY = new Float64Array(PRED_N);
  private predZ = new Float64Array(PRED_N);
  private rollDecel: number;
  private shootBias: number;
  private direct: number;
  /** Defensive effectiveness multiplier (lower = more open, chance-heavy games). */
  private defK: number;

  constructor(config: MatchConfig) {
    this.config = config;
    this.rng = createRng(((config.seed >>> 0) ^ 0x9e3779b9) >>> 0);
    this.trng = createRng(((config.seed >>> 0) * 7 + 13) >>> 0);
    this.drama = clamp(config.drama, 0, 1);
    this.rollDecel = config.weather === 'rain' ? 1.6 : config.weather === 'snow' ? 2.6 : 2.0;
    this.shootBias = 1.3 + this.drama * 0.5;
    this.direct = 1.5 + this.drama * 0.8;
    this.defK = 1.02 - this.drama * 0.5;
    this.home = this.makeTeam(config.home, 'home', 1);
    this.away = this.makeTeam(config.away, 'away', -1);
    this.home.other = this.away; this.away.other = this.home;
    this.all = [...this.home.players, ...this.away.players];
    this.all.forEach((p, i) => { p.i = i; });
    this.kickoffFirst = this.rng.chance(0.5) ? this.home : this.away;
    this.kickoffTeam = this.kickoffFirst;
    this.placeForKickoff(true);
    this.emit('crowd_moment', { pos: { x: 0, y: 8, z: 45 }, text: this.say('crowd_start', { venue: config.venue }), importance: 0.3 });
  }

  get finished(): boolean { return this._finished; }
  get events(): readonly MatchEvent[] { return this._events; }

  // -------------------------------------------------------------------------
  // Setup
  // -------------------------------------------------------------------------

  private makeTeam(prof: TeamProfile, side: TeamSide, dir: 1 | -1): TeamRt {
    const team: TeamRt = {
      side, prof, dir, players: [], other: null as unknown as TeamRt, benchLeft: [...prof.bench], subPlan: [],
      shots: 0, onTarget: 0, corners: 0, fouls: 0, yellow: 0, red: 0, passes: 0, passesOk: 0, xg: 0, possT: 0,
      dugoutX: side === 'home' ? -8 : 8,
    };
    const slots = FORMATIONS[prof.formation];
    prof.players.slice(0, 11).forEach((pp, i) => team.players.push(this.makePlayer(pp, team, slots[i] ?? slots[slots.length - 1]!)));
    const nSubs = this.rng.chance(0.08 + this.drama * 0.12) ? 2 : this.rng.chance(0.55) ? 1 : 0;
    for (let k = 0; k < nSubs; k++) team.subPlan.push(this.rng.range(3600, 5100));
    team.subPlan.sort((a, b) => a - b);
    return team;
  }

  private makePlayer(prof: PlayerProfile, team: TeamRt, slot: SlotDef): SP {
    const b = clamp((prof.rating - 40) / 59, 0, 1);
    const v = () => this.rng.gaussian(0, 0.08);
    const pos = prof.position;
    const isGK = slot.role === 'GK';
    const attr = (bonus: number) => clamp(b * 0.8 + 0.15 + bonus + v(), 0.05, 1);
    const sp: SP = {
      i: 0,
      st: {
        id: prof.id, team: team.side, number: prof.number, pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 },
        heading: 0, anim: 'idle', animPhase: 0, hasBall: false,
      },
      prof, team, slot, isGK,
      pace: attr(pos === 'FWD' ? 0.08 : pos === 'GK' ? -0.2 : 0),
      passing: attr(pos === 'MID' ? 0.1 : pos === 'GK' ? -0.15 : 0),
      shooting: attr(pos === 'FWD' ? 0.15 : pos === 'MID' ? 0.02 : pos === 'DEF' ? -0.15 : -0.4),
      tackling: attr(pos === 'DEF' ? 0.15 : pos === 'FWD' ? -0.15 : 0),
      dribbling: attr(pos === 'FWD' ? 0.1 : pos === 'DEF' ? -0.1 : 0.03),
      keeping: isGK ? attr(0.1) : 0.1,
      heading: attr(slot.role === 'CB' || slot.role === 'ST' ? 0.15 : 0),
      aggression: clamp(this.rng.range(0.15, 0.9) + (pos === 'DEF' ? 0.1 : 0), 0, 1),
      tx: 0, tz: 0, want: 0, direct: false,
      animLock: 0, animLen: 1, lockedAnim: 'idle',
      stun: 0, decideT: 0, pending: null, windup: 0,
      supT: 0, sx: 0, sz: 0, runT: 0, dribX: 1, dribZ: 0, holding: false, carryT: 0, tackleCd: 0,
      yellow: 0, off: false, subOff: false, diveY: 0, stamina: 1, ignoreBall: 0, attempted: -1,
      ix: 0, iz: 0, itime: 99, sepX: 0, sepZ: 0,
      touches: 0, passes: 0, shots: 0, dist: 0, topSpeed: 0,
    };
    this.everPlayed.set(prof.id, sp);
    return sp;
  }

  // -------------------------------------------------------------------------
  // Utility
  // -------------------------------------------------------------------------

  private say(key: Line, v: TextVars = {}): string { return line(this.trng, key, v); }
  private scoreText(): string { return `${this.score.home}-${this.score.away}`; }
  private fx(p: SP): number { return p.st.pos.x * p.team.dir; }
  private fz(p: SP): number { return p.st.pos.z * p.team.dir; }
  private active(team: TeamRt): SP[] { return team.players.filter((p) => !p.off); }
  private gk(team: TeamRt): SP | null { const g = team.players[0]!; return g.isGK && !g.off ? g : null; }

  private lockAnim(p: SP, anim: PlayerAnim, dur: number): void {
    p.lockedAnim = anim; p.animLock = dur; p.animLen = dur;
  }

  private emit(type: MatchEventType, o: EmitOpts): MatchEvent {
    const d = EV[type];
    let subject: string | null;
    if (o.subject !== undefined) subject = o.subject;
    else if (d[3] === 'player') subject = o.player?.prof.id ?? null;
    else if (d[3] === 'second') subject = o.second?.prof.id ?? null;
    else if (d[3] === 'ball') subject = 'ball';
    else subject = null;
    const ev: MatchEvent = {
      id: this.nextEventId++, t: this.t, matchClock: Math.round(this.clock), type,
      team: o.team !== undefined ? o.team : o.player?.team.side ?? null,
      playerId: o.player?.prof.id ?? null,
      secondaryPlayerId: o.second?.prof.id ?? null,
      pos: o.pos ? { ...o.pos } : { ...this.bp },
      importance: o.importance ?? d[0],
      ideal: {
        windowSeconds: o.window ?? d[1], shotSizes: o.sizes ?? d[2], subjectId: subject,
        replayWorthy: o.replay ?? d[4],
        ...((o.graphics ?? d[5]) ? { graphics: o.graphics ?? d[5] } : {}),
      },
      text: o.text,
    };
    this._events.push(ev);
    this.stepEvents.push(ev);
    return ev;
  }

  private schedule(delay: number, fn: () => void, tag?: string): void { this.scheduled.push({ t: this.t + delay, fn, ...(tag ? { tag } : {}) }); }

  private nearestOpp(p: SP): { o: SP | null; d: number } {
    let best: SP | null = null, bd = 1e9;
    for (const o of p.team.other.players) {
      if (o.off) continue;
      const d = Math.hypot(o.st.pos.x - p.st.pos.x, o.st.pos.z - p.st.pos.z);
      if (d < bd) { bd = d; best = o; }
    }
    return { o: best, d: bd };
  }

  private oppDistAt(team: TeamRt, x: number, z: number): number {
    let bd = 1e9;
    for (const o of team.other.players) {
      if (o.off) continue;
      const d = Math.hypot(o.st.pos.x - x, o.st.pos.z - z);
      if (d < bd) bd = d;
    }
    return bd;
  }

  /** 0..1 probability-ish that a ground ball from a to b gets through. */
  private laneSafety(team: TeamRt, ax: number, az: number, bx: number, bz: number, speed: number): number {
    const dx = bx - ax, dz = bz - az;
    const L = Math.hypot(dx, dz) || 1;
    const ux = dx / L, uz = dz / L;
    let safe = 1;
    for (const o of team.other.players) {
      if (o.off) continue;
      const ox = o.st.pos.x - ax, oz = o.st.pos.z - az;
      const u = ox * ux + oz * uz;
      if (u < 0.5 || u > L + 1.5) continue;
      const h = Math.abs(ox * uz - oz * ux);
      const tb = u / speed;
      const reach = 0.9 + 3.2 * Math.max(0, tb - 0.15);
      if (h < reach) safe *= 1 - 0.68 * clamp(1 - (h - 0.4) / (reach - 0.4), 0, 1);
    }
    return safe;
  }

  // -------------------------------------------------------------------------
  // Positioning helpers
  // -------------------------------------------------------------------------

  private kickoffSpot(p: SP, kicking: boolean): [number, number] {
    if (p.isGK) return [-50.5, 0];
    const s = p.slot;
    let x = Math.min(-1.2, -21 + s.x * 0.9);
    let z = s.z * 0.95;
    if (!kicking) {
      const d = Math.hypot(x, z);
      if (d < 10) { const k = 10 / Math.max(d, 0.1); x *= k; z *= k; x = Math.min(x, -1.2); }
    }
    return [x, z];
  }

  private kickoffTaker(team: TeamRt): SP {
    const act = this.active(team);
    let best = act[act.length - 1]!, bv = -1e9;
    for (const p of act) {
      if (p.isGK) continue;
      const v = p.slot.x - Math.abs(p.slot.z) * 0.3;
      if (v > bv) { bv = v; best = p; }
    }
    return best;
  }

  /** Set kickoff targets (and teleport if requested). */
  private placeForKickoff(teleport: boolean): void {
    const taker = this.kickoffTaker(this.kickoffTeam);
    for (const team of [this.home, this.away]) {
      const kicking = team === this.kickoffTeam;
      for (const p of team.players) {
        if (p.off) continue;
        let [x, z] = this.kickoffSpot(p, kicking);
        if (p === taker) { x = -0.35; z = 0.2; }
        p.tx = x * team.dir; p.tz = z * team.dir; p.want = 6; p.direct = true;
        if (teleport) {
          p.st.pos.x = p.tx; p.st.pos.z = p.tz; p.st.vel.x = 0; p.st.vel.z = 0;
          p.st.heading = team.dir > 0 ? 0 : Math.PI;
        }
      }
    }
    if (teleport) { this.bp = { x: 0, y: BR, z: 0 }; this.bv = { x: 0, y: 0, z: 0 }; }
    this.restart = { kind: 'kickoff', team: this.kickoffTeam, x: 0, z: 0, direct: false, taker };
  }

  // -------------------------------------------------------------------------
  // Main step
  // -------------------------------------------------------------------------

  step(dt: number): MatchEvent[] {
    this.stepEvents = [];
    if (this._finished) {
      this.phaseT += dt;
      this.updateEndAnims();
      this.movePlayers(dt);
      return [];
    }
    this.t += dt;
    this.phaseT += dt;
    this.updateClock();

    // scheduled callbacks
    if (this.scheduled.length) {
      const due = this.scheduled.filter((s) => s.t <= this.t);
      if (due.length) {
        this.scheduled = this.scheduled.filter((s) => s.t > this.t);
        for (const s of due) s.fn();
      }
    }

    for (const p of this.all) {
      if (p.animLock > 0) p.animLock -= dt;
      if (p.stun > 0) p.stun -= dt;
      if (p.ignoreBall > 0) p.ignoreBall -= dt;
      if (p.tackleCd > 0) p.tackleCd -= dt;
    }

    switch (this.phase) {
      case 'pre_match':
        this.holdPositions();
        if (this.phaseT > 3) this.setPhase('kickoff');
        break;
      case 'kickoff': this.updateKickoff(dt); break;
      case 'open_play': this.updateOpenPlay(dt); break;
      case 'stoppage': this.updateStoppage(); break;
      case 'set_piece': this.updateSetPiece(); break;
      case 'goal_celebration': this.updateCelebration(); break;
      case 'half_time': this.updateHalfTime(); break;
      case 'full_time': break;
    }

    this.movePlayers(dt);
    this.updateBall(dt);
    this.updateOfficials(dt);
    this.updateIntensity(dt);
    this.checkHalfEnd();
    return this.stepEvents;
  }

  private setPhase(ph: MatchPhase): void { this.phase = ph; this.phaseT = 0; }

  private updateClock(): void {
    if (this.clockFrozen) return;
    const rate = CLOCK_HALF / this.config.halfLengthSeconds;
    const base = this.half === 1 ? 0 : CLOCK_HALF;
    this.clock = this.halfStartT < 0 ? base : base + (this.t - this.halfStartT) * rate;
    if (!this.addedSet && this.clock >= base + CLOCK_HALF) {
      this.addedSet = true;
      const goalsHalf = this.score.home + this.score.away;
      const subs = this.home.subPlan.length + this.away.subPlan.length;
      this.added[this.half - 1] = this.half === 1
        ? 60 * this.rng.int(1, 3)
        : 60 * clamp(this.rng.int(2, 4) + Math.floor(goalsHalf / 3) + (subs > 3 ? 1 : 0), 2, 7);
    }
  }

  private checkHalfEnd(): void {
    if (!this.addedSet || this.clockFrozen) return;
    const end = (this.half === 1 ? CLOCK_HALF : 2 * CLOCK_HALF) + this.added[this.half - 1]!;
    if (this.clock < end) return;
    const ok = this.phase === 'stoppage' || this.phase === 'set_piece' ||
      (this.phase === 'open_play' && (!this.flight?.shot) && (Math.abs(this.bp.x) < 30 || this.clock > end + 40));
    if (!ok) return;
    this.clockFrozen = true;
    this.owner = null; this.flight = null;
    if (this.half === 1) {
      this.setPhase('half_time');
      this.emit('half_time', {
        team: null, pos: { x: 0, y: 0, z: 0 },
        text: this.say('half_time', { home: this.config.home.name, away: this.config.away.name, s: this.scoreText() }),
      });
    } else {
      this.setPhase('full_time');
      this._finished = true;
      this.emit('full_time', {
        team: null, pos: { x: 0, y: 0, z: 0 },
        text: this.say('full_time', { home: this.config.home.name, away: this.config.away.name, s: this.scoreText() }),
      });
      this.updateEndAnims();
    }
  }

  private updateEndAnims(): void {
    const diff = this.score.home - this.score.away;
    for (const p of this.all) {
      if (p.off) continue;
      const res = p.team.side === 'home' ? diff : -diff;
      p.tx = p.st.pos.x; p.tz = p.st.pos.z; p.want = 0;
      if (p.animLock <= 0 && this.phaseT < 12) {
        if (res > 0) this.lockAnim(p, 'celebrate', 3);
        else if (res < 0) this.lockAnim(p, 'dejected', 3);
      }
    }
  }

  private holdPositions(): void {
    for (const p of this.all) { p.want = 2; p.direct = true; }
  }

  // -------------------------------------------------------------------------
  // Kickoff
  // -------------------------------------------------------------------------

  private updateKickoff(_dt: number): void {
    const r = this.restart;
    if (!r || r.kind !== 'kickoff') { this.placeForKickoff(false); return; }
    if (this.phaseT < 0.05) this.placeForKickoff(false);
    const walkOut = this.half === 2 && this.halfStartT < 0;
    for (const p of this.all) { if (p.off) { this.offTarget(p); continue; } p.direct = true; p.want = walkOut ? 7.2 : this.phaseT > 2.5 ? 7.5 : 6.5; }
    let ready = true;
    for (const p of this.all) {
      if (p.off) continue;
      if (Math.hypot(p.st.pos.x - p.tx, p.st.pos.z - p.tz) > 1.6) { ready = false; break; }
    }
    if (!this.ballMove && Math.hypot(this.bp.x, this.bp.z) > 0.2) this.startBallMove(0, 0, 1.4);
    const minT = this.halfStartT < 0 ? 1.5 : 1.0;
    // after a goal, kick off once the two teams are roughly back (stragglers jog in)
    const tk = r.taker;
    const takerReady = !tk || Math.hypot(tk.st.pos.x - tk.tx, tk.st.pos.z - tk.tz) < 0.8;
    const maxWait = walkOut ? 14 : 5.5;
    if (takerReady && !this.ballMove && ((ready && this.phaseT > minT) || this.phaseT > maxWait)) this.takeKickoff();
    else if (this.phaseT > maxWait + 6.5) this.takeKickoff();
  }

  private takeKickoff(): void {
    const team = this.kickoffTeam;
    const taker = this.restart?.taker ?? this.kickoffTaker(team);
    this.bp = { x: 0, y: BR, z: 0 }; this.bv = { x: 0, y: 0, z: 0 }; this.ballMove = null; this.inNet = false;
    // receiver: nearest midfielder behind
    let recv: SP | null = null, bd = 1e9;
    for (const p of this.active(team)) {
      if (p === taker || p.isGK) continue;
      const d = Math.hypot(p.st.pos.x, p.st.pos.z);
      if (this.fx(p) < -4 && d < bd) { bd = d; recv = p; }
    }
    const firstOfHalf = this.halfStartT < 0;
    if (firstOfHalf) this.halfStartT = this.t;
    this.setPhase('open_play');
    this.lastRestartT = this.t;
    this.owner = taker; taker.st.hasBall = true; this.lastTouch = taker; this.possSide = team.side;
    // tiny settle onto the spot (he's already within reach; never a visible jump)
    const kx = -0.35 * team.dir, kz = 0.2 * team.dir;
    const kd = Math.hypot(kx - taker.st.pos.x, kz - taker.st.pos.z);
    if (kd < 1.2) { taker.st.pos.x = kx; taker.st.pos.z = kz; }
    if (recv) {
      taker.pending = { kind: 'kickoff', to: recv, x: recv.st.pos.x, z: recv.st.pos.z }; taker.windup = 0.15;
      this.lockAnim(taker, 'kick', 0.4);
    } else taker.decideT = 0.3;
    const key: Line = firstOfHalf ? (this.half === 1 ? 'kickoff' : 'kickoff_second') : 'kickoff_restart';
    this.emit('kickoff', { team: team.side, player: taker, pos: { x: 0, y: 0, z: 0 }, text: this.say(key, { team: team.prof.name, venue: this.config.venue }) });
  }

  // -------------------------------------------------------------------------
  // Open play
  // -------------------------------------------------------------------------

  private updateOpenPlay(dt: number): void {
    this.computeOffsideLines();
    const owner = this.owner;
    if (owner) {
      this.possSide = owner.team.side;
      owner.team.possT += dt;
    } else if (this.lastTouch) {
      this.lastTouch.team.possT += dt;
    }

    // Counter-attack detection: quick, direct progress after a turnover with the defence outnumbered
    const ctrTeam = owner ? owner.team : this.flight && this.flight.kind !== 'shot' && this.flight.kind !== 'loose' ? this.flight.from.team : null;
    if (!this.counterEmitted && ctrTeam && ctrTeam.side === this.possSide && this.t - this.turnoverT < 6.5) {
      const team = ctrTeam;
      const bfx = this.bp.x * team.dir;
      if (bfx - this.turnoverFx > 20 && bfx > 0 && this.turnoverFx < 12) {
        let goalSide = 0;
        for (const o of this.active(team.other)) if (o.st.pos.x * team.dir > bfx) goalSide++;
        const ctrP = owner ?? this.flight?.intended ?? this.flight!.from;
        if (goalSide <= 5) {
          this.counterEmitted = true;
          this.counterUntil = this.t + 6;
          this.emit('counter_attack', { team: team.side, player: ctrP, text: this.say('counter', { team: team.prof.name, p: surname(ctrP) }) });
        }
      }
    }
    // sustained pressure gets the crowd going
    for (const side of ['home', 'away'] as const) {
      const team = side === 'home' ? this.home : this.away;
      const attacking = this.possSide === side && this.bp.x * team.dir > 18;
      this.pressT[side] = attacking ? this.pressT[side] + dt : Math.max(0, this.pressT[side] - dt * 2);
      if (this.pressT[side] > 7 && this.t - this.lastCrowdT > 40) {
        this.lastCrowdT = this.t; this.pressT[side] = 0;
        this.emit('crowd_moment', { team: side, pos: { x: team.dir * 60, y: 8, z: 0 }, importance: 0.3, text: this.say('crowd_pressure', { team: team.prof.name }) });
      }
    }
    if (this.counterUntil > 0 && owner && owner.team.side !== this.possSideAtCounter()) this.counterUntil = -1;
    if (this.calm && this.t >= this.calm.until) this.calm = null;
    if (this.calm && owner && this.fx(owner) > 30 && this.calm.until - this.t < 14) this.calm.until = Math.min(this.calm.until, this.t + 2); // the attack is on

    this.assignTargets(dt);
    if (owner) this.updateCarrier(owner, dt);
  }

  /** A lull: both sides keep the ball patiently (the spell started by `calm.team`). */
  private calmFor(_team: TeamRt): boolean { return !!this.calm && this.t < this.calm.until; }

  private possSideAtCounter(): TeamSide {
    const e = this._events;
    for (let i = e.length - 1; i >= 0; i--) if (e[i]!.type === 'counter_attack') return e[i]!.team as TeamSide;
    return this.possSide;
  }

  private computeOffsideLines(): void {
    for (const team of [this.home, this.away]) {
      // line in `team`'s attacking frame: second-last opponent
      let a = -1e9, b = -1e9;
      for (const o of team.other.players) {
        if (o.off) continue;
        const fx = o.st.pos.x * team.dir;
        if (fx > a) { b = a; a = fx; } else if (fx > b) b = fx;
      }
      const ballFx = this.bp.x * team.dir;
      this.offLine[team.side] = Math.max(b, ballFx, 0);
    }
  }

  /** Off-ball target assignment for every player in open play. */
  private assignTargets(dt: number): void {
    const owner = this.owner;
    const possTeam = owner ? owner.team : this.flight ? this.flight.from.team : (this.possSide === 'home' ? this.home : this.away);
    const loose = !owner;
    if (loose) this.predictBall();

    for (const team of [this.home, this.away]) {
      const inPoss = team === possTeam;
      const dir = team.dir;
      const bfx = this.bp.x * dir, bfz = this.bp.z * dir;
      const line = this.offLine[team.side];
      const counter = this.counterUntil > this.t;
      // defending deep: the line drops toward the six-yard box as the ball approaches (lets runners into the box)
      this.defLine[team.side] = !inPoss && bfx < -8 ? clamp(-HL + 5 + (bfx + HL) * 0.3, -47, -24) : -99;
      for (const p of team.players) {
        if (p.off) { this.offTarget(p); continue; }
        if (p === owner) continue;
        p.direct = false;
        if (p.isGK) { this.keeperTarget(p, inPoss, bfx, bfz); continue; }
        const s = p.slot;
        let bc: number, depth: number, wm: number, shift: number;
        if (inPoss) { bc = clamp(bfx * 0.68 + 9, -18, 30); depth = 1.3; wm = 1.1; shift = 0.15; }
        else { bc = clamp(bfx * 0.58 - 11, -32, 10); depth = 0.8; wm = 0.66; shift = 0.38; }
        let x = bc + s.x * depth;
        let z = s.z * wm + bfz * shift;
        if (inPoss) {
          if ((s.role === 'FB' || s.role === 'WB') && bfx > -5) {
            x += 7 + (Math.sign(s.z) === Math.sign(bfz) && Math.abs(bfz) > 8 ? 7 : 0);
            z = Math.sign(s.z) * Math.max(Math.abs(z), 27);
          }
          if (s.role === 'W' || s.role === 'WM') { z = Math.sign(s.z) * Math.max(Math.abs(z), 26); x += s.role === 'WM' ? 9 : 4; }
          if (s.role === 'CM' || s.role === 'AM') x += bfx > 20 ? 8 : 3;
          if (s.role === 'ST' || s.role === 'W' || s.role === 'AM') x += 4 + (counter ? 8 : 0);
          // final third: forwards attack the box (near post / far post / penalty spot), far winger to the back post
          const finalThird = owner && owner.team === team && bfx > 14;
          const wideBall = Math.abs(bfz) > 10;
          const boxRunner = s.role === 'ST' || (s.role === 'AM' && wideBall) ||
            ((s.role === 'W' || s.role === 'WM') && (wideBall ? Math.sign(s.z) !== Math.sign(bfz) : bfx > 24));
          if (finalThird && boxRunner) {
            const slotN = (p.i % 3);
            x = Math.min(clamp(bfx + 14, 39, 47) - slotN * 2.5, line - 0.4);
            z = wideBall ? [-3, 4, 9 * -Math.sign(bfz)][slotN]! : [-5, 5, Math.sign(s.z || 1) * 10][slotN]!;
          } else if (p.runT > 0) {
            p.runT -= dt;
            x = Math.min(line + 9, 47); z = z * 0.55;
          } else {
            x = Math.min(x, Math.max(line - 0.7, 0));
            if ((s.role === 'ST' || s.role === 'W' || s.role === 'AM') && owner && owner.team === team &&
              this.fx(owner) > -15 && this.fx(owner) < 40 && Math.abs(this.fx(p) - line) < 5 && this.rng.chance(0.05)) {
              p.runT = 2.2;
            }
          }
          // support movement near carrier
          if (owner && owner.team === team) {
            const d = Math.hypot(p.st.pos.x - owner.st.pos.x, p.st.pos.z - owner.st.pos.z);
            if (d < 26 && p.runT <= 0) {
              p.supT -= dt;
              if (p.supT <= 0) { p.supT = 0.8 + this.rng.next() * 0.4; this.pickSupport(p, owner, x, z); }
              x += p.sx; z += p.sz;
            } else { p.sx *= 0.98; p.sz *= 0.98; }
          }
        } else {
          if (s.role === 'ST') x += 5;
          if (s.pos === 'DEF') x = Math.max(x, -47.5);
          // defenders don't step past the ball when it's deep
          if (s.pos === 'DEF' && bfx < -15) x = Math.min(x, bfx - 1.5);
          const dl = this.defLine[team.side];
          if (s.pos === 'DEF' && dl > -99) x = Math.min(x, dl + (s.role === 'FB' || s.role === 'WB' ? 1.5 : 0));
        }
        x = clamp(x, -49, 49); z = clamp(z, -32, 32);
        const wx = x * dir, wz = z * dir;
        const k = damp(3, dt);
        p.tx += (wx - p.tx) * k; p.tz += (wz - p.tz) * k;
        const dd = Math.hypot(p.tx - p.st.pos.x, p.tz - p.st.pos.z);
        p.want = dd > 12 ? 7.0 : dd > 4 ? 5.4 : 3.6;
        if (counter && inPoss && x > this.fx(p) + 5) p.want = 8;
        else if (inPoss && x > this.fx(p) + 7 && bfx > 0) p.want = Math.max(p.want, 7.6); // bomb forward to join the attack
        if (!inPoss && x < this.fx(p) - 6) p.want = 7; // recover goal-side
        else if (!inPoss && s.pos === 'DEF' && this.defLine[team.side] > -99 && this.fx(p) > this.defLine[team.side] + 1) p.want = Math.max(p.want, 6.2); // drop with the line
        // transition lag: attackers who just lost the ball are slow to react
        if (!inPoss && this.t - this.turnoverT < 2.2 + this.drama && s.pos !== 'DEF') p.want = Math.min(p.want, 3.2);
      }
      if (!inPoss) this.applyMarking(team, owner);
    }

    // pressing
    if (owner && !(this.keeperHoldT > 0 && owner.isGK)) this.applyPress(owner.team.other, owner);
    else { this.pressers.home = null; this.pressers.away = null; }

    // loose ball chasing
    if (loose && !(this.flight?.shot && !this.flight.shot.resolved)) this.applyChase();
  }

  /** Sent-off players trudge to the tunnel mouth by their dugout and stay there. */
  private offTarget(p: SP): void {
    p.tx = p.team.dugoutX * 0.45 + (p.i % 3) * 0.6 * Math.sign(p.team.dugoutX); p.tz = TUNNEL_Z - 2.5; p.want = 2.5; p.direct = true;
  }

  private keeperTarget(p: SP, inPoss: boolean, bfx: number, bfz: number): void {
    const dB = Math.hypot(bfx + HL, bfz);
    const out = clamp(dB * 0.1, 0.8, inPoss ? 12 : 5.5);
    const ang = Math.atan2(bfz, bfx + HL);
    const gx = -HL + Math.cos(ang) * out;
    const gz = clamp(Math.sin(ang) * out, -3.4, 3.4);
    p.tx = gx * p.team.dir; p.tz = gz * p.team.dir; p.want = 4.5; p.direct = true;
  }

  private pickSupport(p: SP, owner: SP, baseX: number, baseZ: number): void {
    const dir = p.team.dir;
    let bestS = -1e9, bx = 0, bz = 0;
    const cands = [[0, 0], [5, 0], [-5, 0], [0, 5], [0, -5], [4, 4], [4, -4], [-4, 4], [-4, -4]] as const;
    for (const [ox, oz] of cands) {
      const fx = baseX + ox, fz = baseZ + oz;
      if (Math.abs(fz) > 32 || fx > 49) continue;
      const wx = fx * dir, wz = fz * dir;
      const od = Math.min(this.oppDistAt(p.team, wx, wz), 9);
      const lane = this.laneSafety(p.team, owner.st.pos.x, owner.st.pos.z, wx, wz, 14);
      const dc = Math.hypot(wx - owner.st.pos.x, wz - owner.st.pos.z);
      const s = od + lane * 4 - Math.hypot(ox, oz) * 0.25 - (dc < 7 ? 3 : 0) - (dc > 30 ? 2 : 0) + (fx - this.fx(owner)) * 0.04;
      if (s > bestS) { bestS = s; bx = ox; bz = oz; }
    }
    p.sx = bx; p.sz = bz;
  }

  private applyMarking(team: TeamRt, owner: SP | null): void {
    const used = new Set<SP>();
    const dir = team.dir;
    for (const d of team.players) {
      if (d.off || d.isGK || d === this.pressers[team.side]) continue;
      let best: SP | null = null, bd = 11;
      for (const a of team.other.players) {
        if (a.off || a.isGK || a === owner || used.has(a)) continue;
        const dist = Math.hypot(a.st.pos.x - d.tx, a.st.pos.z - d.tz);
        if (dist < bd) { bd = dist; best = a; }
      }
      if (!best) continue;
      used.add(best);
      // goal-side of the attacker
      const mx = best.st.pos.x - dir * 1.8, mz = best.st.pos.z;
      const inBox = best.st.pos.x * -dir > BOX_X - 2 && Math.abs(best.st.pos.z) < BOX_HW + 2;
      const w = ((inBox ? 0.55 : 0.25) * clamp(1 - bd / 11, 0, 1) + (inBox ? 0.15 : 0.05)) * this.defK;
      d.tx += (mx - d.tx) * w; d.tz += (mz - d.tz) * w;
      const dl = this.defLine[team.side];
      if (d.slot.pos === 'DEF' && dl > -99 && d.tx * dir > dl + 1.5) d.tx = (dl + 1.5) * dir;
    }
  }

  private applyPress(team: TeamRt, carrier: SP): void {
    const dir = team.dir;
    let cur = this.pressers[team.side];
    if (cur && (cur.off || cur.stun > 0)) cur = null;
    let best: SP | null = null, bd = 1e9, second: SP | null = null, sd = 1e9;
    for (const p of team.players) {
      if (p.off || p.isGK || p.stun > 0) continue;
      const d = Math.hypot(p.st.pos.x - carrier.st.pos.x, p.st.pos.z - carrier.st.pos.z) - (p === cur ? 3 : 0);
      if (d < bd) { second = best; sd = bd; bd = d; best = p; } else if (d < sd) { sd = d; second = p; }
    }
    this.pressers[team.side] = best;
    const ownGoalX = -dir * HL;
    const gx = ownGoalX - carrier.st.pos.x, gz = -carrier.st.pos.z;
    const gl = Math.hypot(gx, gz) || 1;
    if (best && bd < 16) {
      // close down, but with a jockeying approach (don't fly in)
      const calm = this.calmFor(carrier.team);
      const standOff = calm ? 3.5 : bd > 4 ? 0.8 : 1.0;
      best.tx = carrier.st.pos.x + (gx / gl) * standOff + carrier.st.vel.x * 0.25;
      best.tz = carrier.st.pos.z + (gz / gl) * standOff + carrier.st.vel.z * 0.25;
      best.want = calm ? 4.5 : bd > 6 ? 7.6 : bd > 3 ? 6.6 : 5.6; best.direct = true;
    } else if (best) this.pressers[team.side] = null;
    const carrierFxForDef = carrier.st.pos.x * dir;
    if (second && carrierFxForDef < -15) {
      second.tx = carrier.st.pos.x + (gx / gl) * 6;
      second.tz = carrier.st.pos.z + (gz / gl) * 6;
      second.want = 6.5; second.direct = true;
    }
  }

  private predictBall(): void {
    let x = this.bp.x, y = this.bp.y, z = this.bp.z, vx = this.bv.x, vy = this.bv.y, vz = this.bv.z;
    const dt = PRED_DT;
    for (let k = 0; k < PRED_N; k++) {
      // 3 substeps for accuracy
      for (let s = 0; s < 3; s++) {
        const h = dt / 3;
        if (y > BR + 0.001 || vy > 0.01) {
          vy -= G * h; x += vx * h; y += vy * h; z += vz * h;
          if (y < BR) { y = BR; if (vy < -1.5) { vy = -vy * 0.5; vx *= 0.85; vz *= 0.85; } else vy = 0; }
        } else {
          const sp = Math.hypot(vx, vz);
          if (sp > 0) { const ns = Math.max(0, sp - this.rollDecel * h); vx *= ns / sp; vz *= ns / sp; }
          x += vx * h; z += vz * h;
        }
      }
      this.predX[k] = x; this.predY[k] = y; this.predZ[k] = z;
    }
  }

  private interceptFor(p: SP): void {
    const vmax = (6.8 + p.pace * 1.6) * TEMPO;
    const px = p.st.pos.x, pz = p.st.pos.z;
    const maxY = p.isGK ? 2.6 : 2.3;
    for (let k = 0; k < PRED_N; k++) {
      const tk = (k + 1) * PRED_DT;
      if (this.predY[k]! > maxY) continue;
      const d = Math.hypot(this.predX[k]! - px, this.predZ[k]! - pz) - 0.8;
      if (d <= vmax * Math.max(0, tk - 0.15)) { p.ix = this.predX[k]!; p.iz = this.predZ[k]!; p.itime = tk; return; }
    }
    const lx = this.predX[PRED_N - 1]!, lz = this.predZ[PRED_N - 1]!;
    p.ix = lx; p.iz = lz; p.itime = PRED_N * PRED_DT + Math.hypot(lx - px, lz - pz) / vmax;
  }

  /** Chase target: the intercept point, but once close go straight at the ball (don't shadow it). */
  private chaseTo(p: SP, want: number): void {
    const dx = this.bp.x - p.st.pos.x, dz = this.bp.z - p.st.pos.z;
    const d = Math.hypot(dx, dz);
    if (d < 4 && d > 0.05 && this.bp.y < 1.6) {
      // aim through the ball (target past it) so arrival braking doesn't leave him jogging alongside
      p.tx = this.bp.x + this.bv.x * 0.3 + (dx / d) * 1.5; p.tz = this.bp.z + this.bv.z * 0.3 + (dz / d) * 1.5;
    } else { p.tx = p.ix; p.tz = p.iz; }
    p.want = want; p.direct = true;
  }

  private applyChase(): void {
    const f = this.flight;
    const bestOf: Record<TeamSide, SP | null> = { home: null, away: null };
    for (const team of [this.home, this.away]) {
      let best: SP | null = null;
      for (const p of team.players) {
        if (p.off || p.stun > 0) continue;
        this.interceptFor(p);
        if (p.isGK) {
          const fx = p.ix * team.dir;
          if (!(fx < -HL + 16.5 && Math.abs(p.iz) < BOX_HW)) continue;
          // crosses: only come for balls into the six-yard area (or occasionally a brave claim)
          if (f && f.kind === 'cross' && f.from.team !== team && fx > -HL + 6 && !(f.id % 5 === 0)) continue;
        }
        if (p.ignoreBall > 0) continue;
        if (this.scramble && this.t < this.scramble.until && this.scramble.team === team) p.itime -= 0.35;
        if (!best || p.itime < best.itime) best = p;
      }
      bestOf[team.side] = best;
    }
    const recv = f?.intended && !f.intended.off ? f.intended : null;
    for (const team of [this.home, this.away]) {
      const b = bestOf[team.side];
      const oppBest = bestOf[team.other.side];
      if (recv && recv.team === team) {
        this.chaseTo(recv, 8.5);
        if (b && b !== recv && b.itime < recv.itime - 0.5) this.chaseTo(b, 8.5);
        continue;
      }
      if (!b) continue;
      const contest = recv ? b.itime < recv.itime + 0.15 : (!oppBest || b.itime < oppBest.itime + 1.0);
      if (contest) this.chaseTo(b, 8.5);
      else if (recv) {
        // press the receiver instead
        const dir = team.dir;
        b.tx = recv.ix - dir * 1.5; b.tz = recv.iz; b.want = 7.5; b.direct = true;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Carrier
  // -------------------------------------------------------------------------

  private updateCarrier(p: SP, dt: number): void {
    p.st.hasBall = true;
    p.direct = true;
    if (p.pending) {
      if (this.ballPinned) {
        // step up to the dead ball
        const dx = this.bp.x - p.st.pos.x, dz = this.bp.z - p.st.pos.z, d = Math.hypot(dx, dz) || 1;
        p.tx = this.bp.x - (dx / d) * 0.45; p.tz = this.bp.z - (dz / d) * 0.45;
        p.want = Math.max(2, (d - 0.45) / Math.max(0.1, p.windup));
      } else { p.want = 1.5; p.tx = p.st.pos.x + p.st.vel.x * 0.2; p.tz = p.st.pos.z + p.st.vel.z * 0.2; }
      p.windup -= dt;
      if (p.windup <= 0) { const a = p.pending; p.pending = null; this.execute(p, a); }
      return;
    }
    if (p.isGK && this.keeperHoldT > 0) {
      this.keeperHoldT -= dt;
      p.want = 1.2;
      p.tx = (-HL + 6) * p.team.dir; p.tz = clamp(p.st.pos.z, -8, 8);
      if (this.keeperHoldT <= 0) this.keeperDistribute(p);
      return;
    }
    // pressure & tackles
    this.tackleCheck(p);
    if (this.owner !== p) return;

    p.decideT -= dt;
    p.carryT += dt;
    if (p.decideT <= 0) this.decide(p);
    if (p.pending) return;

    if (p.holding) {
      const { o } = this.nearestOpp(p);
      const ax = o ? p.st.pos.x - o.st.pos.x : 0, az = o ? p.st.pos.z - o.st.pos.z : 0;
      const l = Math.hypot(ax, az) || 1;
      p.tx = p.st.pos.x + (ax / l) * 2; p.tz = p.st.pos.z + (az / l) * 2; p.want = 1.8;
    } else {
      p.tx = p.st.pos.x + p.dribX * 5; p.tz = p.st.pos.z + p.dribZ * 5;
      const { d } = this.nearestOpp(p);
      p.want = clamp(5.0 + p.dribbling * 2.0 - (d < 3 ? 1.2 : 0) + (this.counterUntil > this.t ? 0.8 : 0), 3.5, 7.5);
    }
    // don't dribble out of play
    p.tx = clamp(p.tx, -HL + 0.8, HL - 0.8); p.tz = clamp(p.tz, -HW + 0.8, HW - 0.8);
  }

  private tackleCheck(carrier: SP): void {
    let def = this.pressers[carrier.team.other.side];
    if (!def || def.stun > 0 || def.tackleCd > 0 || def.off) {
      // anyone else tight on him can have a go too
      const { o, d: od } = this.nearestOpp(carrier);
      def = o && !o.isGK && od < 2.2 && o.stun <= 0 && o.tackleCd <= 0 ? o : null;
      if (!def) return;
    }
    const d = Math.hypot(def.st.pos.x - carrier.st.pos.x, def.st.pos.z - carrier.st.pos.z);
    if (d > 2.5) return; // lunges / sliding challenges reach a bit further
    def.tackleCd = 0.35;
    const dfx = def.st.pos.x * def.team.dir;
    // pressing game: lots of challenges in midfield, a bit more caution near your own box
    const zoneK = dfx < -BOX_X ? 0.6 : dfx < -17 ? 1.15 : dfx < 17 ? 0.45 : 0.6;
    // during a patient spell the defending side sits off rather than diving in
    const calmK = this.calmFor(carrier.team) ? 0.35 : 1;
    if (!this.rng.chance(Math.min(0.95, (0.5 + def.aggression * 0.35) * zoneK * calmK))) return;
    this.resolveTackle(def, carrier);
  }

  private resolveTackle(def: SP, att: SP): void {
    const diff = def.tackling - att.dribbling;
    const dfx = def.st.pos.x * def.team.dir; // defender frame
    const inOwnBox = dfx < -HL + 16.5 && Math.abs(att.st.pos.z) < BOX_HW;
    const pWin = clamp(0.5 + diff * 0.45 - (inOwnBox ? 0.08 : 0), 0.25, 0.8);
    let pFoul = (0.34 + def.aggression * 0.22 + this.drama * 0.14) * (inOwnBox ? 0.25 : 1) * (this.calmFor(att.team) ? 0.4 : 1);
    if (this.counterUntil > this.t) pFoul *= 1.5;
    pFoul = clamp(pFoul, 0, 0.7);
    const r = this.rng.next();
    this.lockAnim(def, 'tackle', 0.6);
    if (r < pFoul) {
      this.foul(def, att);
      return;
    }
    if (r < pFoul + pWin * (1 - pFoul)) {
      // won it
      const bigOne = (dfx < -30) || this.counterUntil > this.t;
      if (this.rng.chance(0.4)) {
        // poked loose
        const a = this.rng.range(-1.2, 1.2) + (def.team.dir > 0 ? 0 : Math.PI);
        const sp = this.rng.range(3, 7);
        this.releaseBall(def, 'loose', null);
        this.bv = { x: Math.cos(a) * sp, y: 0, z: Math.sin(a) * sp };
        def.ignoreBall = 0.2;
      } else {
        this.owner = null;
        this.gain(def, false);
      }
      att.stun = 0.4;
      this.counterUntil = -1;
      this.emit('tackle', {
        player: def, second: att, importance: bigOne ? 0.45 : 0.22, replay: bigOne,
        text: this.say(bigOne ? 'tackle_big' : 'tackle', { p: surname(att), q: surname(def) }),
      });
      return;
    }
    // beaten — sometimes he just hauls the man down
    const afx0 = this.fx(att);
    if (this.rng.chance(0.18 + def.aggression * 0.15 + (afx0 > 0 ? 0.08 : 0))) {
      this.cynical = true;
      this.foul(def, att);
      return;
    }
    def.stun = 0.9 + this.rng.next() * 0.4;
    att.decideT = Math.min(att.decideT, 0.25);
    const afx = this.fx(att);
    if (afx > 0) {
      this.emit('dribble', {
        player: att, second: def, importance: afx > 30 ? 0.35 : 0.2,
        text: this.say('dribble', { p: surname(att), q: surname(def) }),
      });
    }
  }

  private decide(p: SP): void {
    const T = p.team, dir = T.dir;
    const px = p.st.pos.x, pz = p.st.pos.z;
    const pfx = px * dir, pfz = pz * dir;
    const { d: oppD } = this.nearestOpp(p);
    const pressure = clamp(1 - (oppD - 1) / 5, 0, 1);
    const line = this.offLine[T.side];
    let bestU = -1e9;
    let best: Action | null = null;
    let bestDrib = false;
    const consider = (u: number, a: Action | null): void => {
      u += this.rng.next() * 0.12;
      if (u > bestU) { bestU = u; best = a; bestDrib = a === null; }
    };
    const myThreat = threat(pfx, pfz);
    const calm = this.calmFor(T);
    const lossCost = (0.18 + (1 - clamp((pfx + HL) / (2 * HL), 0, 1)) * 0.45) * (calm ? 2.2 : 1);
    const direct = calm ? 0.5 : this.direct;

    if (p.isGK) {
      // keeper with ball at feet: pass short or go long
      for (const q of this.active(T)) {
        if (q === p) continue;
        const d = Math.hypot(q.st.pos.x - px, q.st.pos.z - pz);
        if (d < 8 || d > 40) continue;
        const lane = this.laneSafety(T, px, pz, q.st.pos.x, q.st.pos.z, 14);
        const open = clamp((this.oppDistAt(T, q.st.pos.x, q.st.pos.z) - 1.5) / 6, 0, 1);
        consider(lane * open * 0.8, { kind: 'pass', to: q, x: q.st.pos.x, z: q.st.pos.z });
      }
      consider(0.3, { kind: 'keeper_punt', to: null, x: 0, z: 0 });
      p.decideT = 0.3;
      if (best) this.queue(p, best);
      return;
    }

    // --- shoot
    const dGoal = Math.hypot(HL - pfx, pfz);
    if (pfx > 16 && dGoal < 25 + this.drama * 3) {
      const xg = xgModel(pfx, pfz) * (1 - 0.3 * pressure);
      const inBox = pfx > BOX_X && Math.abs(pfz) < BOX_HW;
      let u = (xg * 5 + 0.14) * this.shootBias + (dGoal < 14 ? 0.3 : 0) + (inBox ? 0.18 : dGoal < 20.5 ? 0.22 : 0) + (p.shooting - 0.5) * 0.3 - pressure * 0.08;
      // speculative efforts from range: only the shooters, and rarely
      if (dGoal > 20) u = (0.3 + p.shooting * 0.35) * this.shootBias - pressure * 0.1 - (dGoal - 20) * 0.06 - (calm ? 0.5 : 0);
      consider(u, { kind: 'shot', to: null, x: 0, z: 0, shotKind: 'open' });
    }

    // --- passes
    for (const q of T.players) {
      if (q === p || q.off) continue;
      const qx = q.st.pos.x + q.st.vel.x * 0.5, qz = q.st.pos.z + q.st.vel.z * 0.5;
      const d = Math.hypot(qx - px, qz - pz);
      if (d < 5 || d > (calm ? 24 : 55)) continue;
      const qfx = qx * dir, qfz = qz * dir;
      if (q.isGK && pfx > -20) continue;
      const open = clamp((this.oppDistAt(T, qx, qz) - 1.2) / 6, 0, 1);
      const offside = qfx > line + 0.2 && qfx > 0;
      const gain = threat(qfx, qfz) - myThreat;
      if (d <= 32) {
        const lane = this.laneSafety(T, px, pz, qx, qz, 14);
        const succ = clamp(lane * (0.5 + 0.5 * open) * (1 - d / 110) * (0.75 + 0.25 * p.passing), 0, 1);
        const prog = qfx - pfx;
        let u = succ * (0.36 + threat(qfx, qfz) * (calm ? 0.4 : 1.2) + gain * (calm ? 0.2 : 0.7) + (prog > 0 ? prog * 0.022 * direct : prog * (calm ? 0.004 : 0.016))) - (1 - succ) * lossCost * 0.7;
        if (this.lastPasser && this.lastPasser.p === q && this.t - this.lastPasser.t < 3) u -= 0.15;
        if (offside) u -= 0.6;
        consider(u, { kind: 'pass', to: q, x: qx, z: qz });
      }
      if (d > 24) {
        const succ = clamp((0.25 + 0.75 * open) * (0.9 - d / 140) * (0.7 + 0.3 * p.passing), 0, 1);
        let u = succ * (0.26 + threat(qfx, qfz) * 1.2 + gain * 0.6 + Math.max(0, qfx - pfx) * 0.014 * direct) - (1 - succ) * lossCost * 0.6 - 0.05 - (calm ? 0.3 : 0);
        if (offside) u -= 0.6;
        consider(u, { kind: 'long', to: q, x: qx, z: qz });
      }
      // through ball into space for runners / forwards
      const s = q.slot.role;
      if ((s === 'ST' || s === 'W' || s === 'AM' || s === 'WM' || q.runT > 0) && qfx > pfx - 2 && qfx < 46) {
        const tfx = Math.min(qfx + 9, 47), tfz = qfz * 0.8;
        const tx = tfx * dir, tz = tfz * dir;
        const td = Math.hypot(tx - px, tz - pz);
        if (td > 8 && td < 42) {
          const lane = this.laneSafety(T, px, pz, tx, tz, 13);
          const qd = Math.hypot(tx - q.st.pos.x, tz - q.st.pos.z);
          const od = this.oppDistAt(T, tx, tz);
          const race = clamp((od - qd * 0.85) / 6 + 0.5, 0, 1);
          const succ = clamp(lane * race * (0.6 + 0.4 * p.passing), 0, 1);
          const seesOffside = offside && this.rng.chance(0.75 + p.passing * 0.2);
          let u = succ * (0.34 + threat(tfx, tfz) * 2.0 + (tfx - pfx) * 0.012 * direct) - (1 - succ) * lossCost * 0.7 - 0.02 - (calm ? 0.25 : 0);
          if (seesOffside) u -= 0.8;
          consider(u, { kind: 'through', to: q, x: tx, z: tz });
        }
      }
    }

    // --- cross
    if (pfx > 18 && Math.abs(pfz) > 11) {
      let bestT: SP | null = null, bo = -1;
      let n = 0;
      for (const q of T.players) {
        if (q.off || q === p) continue;
        // deliver into the area a runner is arriving in, not just where he stands now
        const qfx = this.fx(q) + Math.max(0, q.st.vel.x * dir) * 0.9, qfz = this.fz(q);
        if (qfx > 30 && Math.abs(qfz) < 18 && q.slot.pos !== 'DEF' && this.fx(q) <= this.offLine[T.side] + 0.2) {
          n++;
          const o = this.oppDistAt(T, q.st.pos.x, q.st.pos.z) + q.heading * 2 + this.rng.next();
          if (o > bo) { bo = o; bestT = q; }
        }
      }
      if (bestT) {
        const u = 0.5 + n * 0.17 + (pfx > 30 ? 0.15 : 0) + (pfx > 40 ? 0.15 : 0) + this.drama * 0.15 - pressure * 0.05;
        if (pfx > 45 && this.rng.chance(0.35)) consider(u, { kind: 'cutback', to: bestT, x: bestT.st.pos.x, z: bestT.st.pos.z });
        else consider(u, { kind: 'cross', to: bestT, x: bestT.st.pos.x, z: bestT.st.pos.z });
      }
    }

    // --- dribble
    let bestDir = 0, bestDU = -1e9;
    const angs = [0, 0.5, -0.5, 1.0, -1.0, 1.5, -1.5];
    for (const a of angs) {
      const fdx = Math.cos(a), fdz = Math.sin(a);
      const tfx = pfx + fdx * 6, tfz = pfz + fdz * 6;
      if (Math.abs(tfz) > 32 || tfx > 51) continue;
      const space = Math.min(this.oppDistAt(T, tfx * dir, tfz * dir), 10);
      const du = space * 0.022 + fdx * (space > 5 ? 0.16 : 0.06) * (calm ? 0.3 : 1.2) + threat(tfx, tfz) * (calm ? 0.1 : tfx > 20 ? 0.75 : 0.3);
      if (du > bestDU) { bestDU = du; bestDir = a; }
    }
    if (bestDU > -1e8) {
      // tempo: in build-up the ball should move rather than be carried
      const carryPen = pfx < 17 ? 0.12 + Math.max(0, p.carryT - 0.8) * 0.2 : Math.max(0, p.carryT - 1.2) * 0.12;
      const u = 0.04 + bestDU + p.dribbling * 0.08 - pressure * 0.3 - carryPen;
      consider(u, null);
    }
    // --- hoofed clearance under pressure in our own third
    if (pfx < -22 && (p.slot.pos === 'DEF' || pfx < -38) && pressure > 0.4) {
      consider(0.12 + pressure * 0.32 + (pfx < -38 ? 0.15 : 0), { kind: 'clear', to: null, x: 0, z: 0 });
    }
    // --- hold
    consider(0.02 - pressure * 0.1, { kind: 'pass', to: null, x: 0, z: 0 });

    const chosen = best as Action | null;
    if (bestDrib || !chosen) {
      p.holding = false;
      p.dribX = Math.cos(bestDir) * dir; p.dribZ = Math.sin(bestDir) * dir;
      p.decideT = pfx > 20 ? 0.18 + this.rng.next() * 0.12 : 0.2 + this.rng.next() * 0.15;
      return;
    }
    if (!chosen.to && chosen.kind === 'pass') {
      p.holding = true; p.decideT = 0.3;
      return;
    }
    p.holding = false;
    this.queue(p, chosen);
  }

  private queue(p: SP, a: Action): void {
    p.pending = a;
    p.windup = a.kind === 'shot' ? 0.2 : a.kind === 'cross' || a.kind === 'long' ? 0.2 : 0.13;
    this.lockAnim(p, 'kick', p.windup + 0.25);
  }

  private keeperDistribute(p: SP): void {
    const T = p.team;
    let best: SP | null = null, bs = -1e9;
    for (const q of this.active(T)) {
      if (q === p) continue;
      const d = Math.hypot(q.st.pos.x - p.st.pos.x, q.st.pos.z - p.st.pos.z);
      if (d < 8 || d > 32) continue;
      const s = this.oppDistAt(T, q.st.pos.x, q.st.pos.z) - d * 0.1 + this.rng.next() * 2;
      if (s > bs) { bs = s; best = q; }
    }
    this.keeperHoldT = 0;
    if (best && bs > 5 && this.rng.chance(0.6)) {
      this.lockAnim(p, 'throw', 0.6);
      p.pending = { kind: 'keeper_throw', to: best, x: best.st.pos.x, z: best.st.pos.z }; p.windup = 0.3;
    } else {
      this.lockAnim(p, 'kick', 0.6);
      p.pending = { kind: 'keeper_punt', to: null, x: 0, z: 0 }; p.windup = 0.3;
    }
  }

  // -------------------------------------------------------------------------
  // Executing actions (ball leaves the foot)
  // -------------------------------------------------------------------------

  private execute(p: SP, a: Action): void {
    if (this.owner !== p) return;
    const T = p.team, dir = T.dir;
    const px = p.st.pos.x, pz = p.st.pos.z;
    const noise = (d: number, skill: number) => this.rng.gaussian(0, d * 0.05 * (1.25 - skill));
    switch (a.kind) {
      case 'shot': this.shoot(p, a.shotKind ?? 'open'); return;
      case 'pass': case 'kickoff': {
        const q = a.to!;
        const d0 = Math.hypot(q.st.pos.x - px, q.st.pos.z - pz);
        const T0 = 0.25 + d0 / 16;
        let tx = q.st.pos.x + q.st.vel.x * T0 * 0.7, tz = q.st.pos.z + q.st.vel.z * T0 * 0.7;
        const d = Math.hypot(tx - px, tz - pz);
        tx += noise(d, p.passing); tz += noise(d, p.passing);
        this.groundPass(p, tx, tz, a.kind === 'kickoff' ? 'kickoff' : 'pass', q, 20);
        return;
      }
      case 'through': {
        const q = a.to!;
        const tx = a.x + noise(20, p.passing), tz = a.z + noise(20, p.passing);
        this.groundPass(p, tx, tz, 'through', q, 15, 6);
        q.runT = Math.max(q.runT, 2.5);
        return;
      }
      case 'long': {
        const q = a.to!;
        const d = Math.hypot(q.st.pos.x - px, q.st.pos.z - pz);
        const tf = 1.0 + d / 28;
        const tx = q.st.pos.x + q.st.vel.x * tf * 0.8 + noise(d, p.passing) * 1.5;
        const tz = q.st.pos.z + q.st.vel.z * tf * 0.8 + noise(d, p.passing) * 1.5;
        this.loftedPass(p, tx, tz, BR, 0.85 + Math.hypot(tx - px, tz - pz) / 30, 'long', q);
        if (d > 35) this.emit('long_ball', { player: p, second: q, text: this.say(Math.abs(q.st.pos.z - pz) > 30 ? 'switch' : 'long_ball', { p: surname(p), q: surname(q) }) });
        return;
      }
      case 'cross': {
        const q = a.to!;
        // a defender closing down the crosser can block it behind
        const { o: blk, d: bd } = this.nearestOpp(p);
        if (blk && bd < 2.8 && !blk.isGK && this.phase === 'open_play' && this.rng.chance(0.6)) {
          this.lockAnim(blk, 'tackle', 0.5);
          this.emit('cross', { player: p, second: q, importance: 0.22, text: this.say('cross', { p: surname(p), q: surname(q) }) });
          this.owner = p; this.releaseBall(p, 'loose', null);
          this.bp.y = 0.3;
          this.deflectBehind(blk);
          return;
        }
        // aim at a header point just in front of the target, towards goal
        const hfx = clamp(this.fx(q) + 5, 40, 46), hfz = clamp(this.fz(q) * 0.7, -7, 7);
        const tx = hfx * dir + noise(25, p.passing), tz = hfz * dir + noise(25, p.passing);
        const d = Math.hypot(tx - px, tz - pz);
        this.loftedPass(p, tx, tz, 1.85, 0.6 + d / 30, 'cross', q);
        q.tx = tx; q.tz = tz;
        this.emit('cross', { player: p, second: q, text: this.say('cross', { p: surname(p), q: surname(q) }) });
        return;
      }
      case 'cutback': {
        const q = a.to!;
        const tx = clamp(this.fx(q), 36, 44) * dir, tz = clamp(this.fz(q), -8, 8) * dir;
        this.groundPass(p, tx, tz, 'cutback', q, 15);
        this.emit('cross', { player: p, second: q, importance: 0.32, text: this.say('cutback', { p: surname(p), q: surname(q) }) });
        return;
      }
      case 'clear': {
        // a panicky clearance from inside the box under pressure is often sliced behind
        if (this.fx(p) < -BOX_X && this.nearestOpp(p).d < 3 && this.phase === 'open_play' && this.rng.chance(0.35)) {
          this.owner = p; this.releaseBall(p, 'loose', null);
          this.deflectBehind(p);
          return;
        }
        const tfx = clamp(p.st.pos.x * dir + this.rng.range(25, 45), -30, 40);
        // hacked clear towards the nearer touchline: often ends in a throw-in
        const tfz = clamp(p.st.pos.z * dir * 1.4 + this.rng.range(-14, 14), -44, 44);
        this.loftedPass(p, tfx * dir, tfz * dir, BR, 2.0 + this.rng.next() * 0.6, 'clear', null);
        return;
      }
      case 'throw': {
        const q = a.to;
        const tx = q ? q.st.pos.x : px + this.rng.range(-10, 10), tz = q ? q.st.pos.z : pz * 0.7;
        this.bp.y = 2.1;
        this.loftedPass(p, tx, tz, BR, 0.7 + Math.hypot(tx - px, tz - pz) / 30, 'throw', q);
        return;
      }
      case 'goal_kick': case 'keeper_punt': {
        let q = a.to;
        if (!q) {
          // pick a target forward
          let bs = -1e9;
          for (const c of this.active(T)) {
            if (c.isGK) continue;
            const s = this.fx(c) * 0.04 + this.oppDistAt(T, c.st.pos.x, c.st.pos.z) * 0.2 + this.rng.next();
            if (this.fx(c) > -15 && s > bs) { bs = s; q = c; }
          }
        }
        const tx = q ? q.st.pos.x + this.rng.gaussian(0, 4) : 10 * dir;
        const tz = q ? q.st.pos.z + this.rng.gaussian(0, 4) : 0;
        if (a.kind === 'keeper_punt') this.bp.y = 0.6;
        this.loftedPass(p, tx, tz, BR, 1.8 + Math.hypot(tx - px, tz - pz) / 40, a.kind === 'goal_kick' ? 'goal_kick' : 'keeper', q);
        return;
      }
      case 'keeper_throw': {
        const q = a.to!;
        this.bp.y = 1.6;
        this.loftedPass(p, q.st.pos.x, q.st.pos.z, BR, 0.6 + Math.hypot(q.st.pos.x - px, q.st.pos.z - pz) / 30, 'keeper', q);
        return;
      }
    }
  }

  private releaseBall(p: SP, kind: FlightKind, intended: SP | null): Flight {
    this.owner = null;
    p.st.hasBall = false;
    p.ignoreBall = 0.3;
    p.holding = false;
    this.lastTouch = p;
    this.keeperHoldT = 0;
    const f: Flight = { id: ++this.flightSeq, kind, from: p, intended, t0: this.t, offside: false, shot: null };
    this.flight = f;
    if (intended && intended.team === p.team && kind !== 'kickoff' && kind !== 'throw' && kind !== 'goal_kick') {
      const ifx = this.fx(intended);
      if (ifx > 0 && ifx > this.offLine[p.team.side] + 0.25) f.offside = true;
    }
    if (kind !== 'loose' && kind !== 'shot' && kind !== 'clear') {
      p.passes++; p.team.passes++;
      this.lastPasser = { p, t: this.t };
    }
    return f;
  }

  private groundPass(p: SP, tx: number, tz: number, kind: FlightKind, q: SP | null, avg: number, arrive = 0): void {
    tx = clamp(tx, -HL - 2, HL + 2); tz = clamp(tz, -HW - 2, HW + 2);
    const dx = tx - this.bp.x, dz = tz - this.bp.z;
    const d = Math.hypot(dx, dz) || 1;
    const T = 0.2 + d / (avg + 2);
    let v0 = Math.min(28, d / T + this.rollDecel * T / 2);
    if (arrive > 0) v0 = Math.min(v0, Math.sqrt(arrive * arrive + 2 * this.rollDecel * d));
    this.releaseBall(p, kind, q);
    this.bp.y = Math.max(BR, this.bp.y); // if still settling from a high touch, gravity brings it down
    this.bv = { x: (dx / d) * v0, y: 0, z: (dz / d) * v0 };
  }

  private loftedPass(p: SP, tx: number, tz: number, yEnd: number, T: number, kind: FlightKind, q: SP | null): void {
    const y0 = Math.max(this.bp.y, BR);
    const dx = tx - this.bp.x, dz = tz - this.bp.z;
    this.releaseBall(p, kind, q);
    this.bv = { x: dx / T, y: (yEnd - y0 + 0.5 * G * T * T) / T, z: dz / T };
    this.bp.y = y0;
  }

  // -------------------------------------------------------------------------
  // Shooting
  // -------------------------------------------------------------------------

  private shoot(p: SP, kind: ShotKind): void {
    const T = p.team, dir = T.dir;
    const keeper = this.gk(T.other);
    const goalX = dir * HL;
    const pfx = this.fx(p), pfz = this.fz(p);
    const dist = Math.hypot(HL - pfx, pfz);
    const { d: oppD } = this.nearestOpp(p);
    const pressure = clamp(1 - (oppD - 1) / 5, 0, 1);
    let xg = kind === 'pen' ? 0.76 : xgModel(pfx, pfz) * (kind === 'header' ? 0.55 : 1) * (1 - 0.3 * pressure);
    if (kind === 'fk') xg = Math.min(xg, 0.07);
    if (kind === 'volley') xg *= 0.85;

    // blocker in the lane?
    let blocker: SP | null = null;
    if (kind !== 'pen') {
      const ux = goalX - p.st.pos.x, uz = -p.st.pos.z;
      const L = Math.hypot(ux, uz);
      let bu = 1e9;
      for (const o of T.other.players) {
        if (o.off || o.isGK) continue;
        const ox = o.st.pos.x - p.st.pos.x, oz = o.st.pos.z - p.st.pos.z;
        const u = (ox * ux + oz * uz) / L;
        if (u < 0.8 || u > Math.min(L - 1.5, 18)) continue;
        const h = Math.abs(ox * uz - oz * ux) / L;
        if (h < 1.5 + u * 0.04 && u < bu) { bu = u; blocker = o; }
      }
    }

    // outcome
    const kq = keeper ? keeper.keeping : 0;
    let pGoal = kind === 'pen'
      ? clamp(0.78 + (p.shooting - 0.5) * 0.15 - (kq - 0.5) * 0.2, 0.6, 0.9)
      : clamp((0.07 + xg * 2.3) * (0.7 + p.shooting * 0.6) * (1.25 - kq * 0.5) * (0.46 + this.drama * 0.46), 0.03, 0.85);
    if (!keeper) pGoal = Math.max(pGoal, 0.8);
    let outcome: ShotOutcome;
    const pBlock = blocker ? (kind === 'fk' ? 0.35 : kind === 'header' ? 0.22 : 0.48) : 0;
    if (this.rng.chance(pBlock)) outcome = 'block';
    else if (this.rng.chance(pGoal)) outcome = 'goal';
    else {
      const pSave = kind === 'pen' ? 0.7 : clamp(0.3 + xg * 0.6 + kq * 0.1, 0.25, 0.6);
      if (keeper && this.rng.chance(pSave)) {
        const r = this.rng.next();
        outcome = r < 0.32 - xg * 0.3 ? 'catch' : r < 0.55 ? 'parry' : 'tip';
      } else {
        const r = this.rng.next();
        const wood = 0.05 + this.drama * 0.05;
        const near = kind === 'pen' ? 0.6 : 0.36 + this.drama * 0.1;
        outcome = r < wood ? (this.rng.chance(0.6) ? 'post' : 'bar') : r < wood + near ? 'near' : 'wide';
      }
    }
    // a shot heading off target through a crowd usually takes a touch (deflected behind for a corner)
    if ((outcome === 'wide' || outcome === 'near') && kind !== 'pen' && kind !== 'fk') {
      if (!blocker) {
        // nobody square in the lane, but someone close enough to stick a leg out
        const { o, d } = this.nearestOpp(p);
        if (o && !o.isGK && d < 5 && this.rng.chance(0.3)) blocker = o;
      }
      if (blocker && this.rng.chance(0.5)) outcome = 'block';
    }
    // a defender on the line can still keep it out
    let liner: SP | null = null;
    if (outcome === 'goal' && kind !== 'pen' && dist < 22) {
      let bd = 6.5;
      for (const o of T.other.players) {
        if (o.off || o.isGK || o.stun > 0) continue;
        const d = Math.hypot(o.st.pos.x - goalX, o.st.pos.z);
        if (d < bd) { bd = d; liner = o; }
      }
      if (liner && this.rng.chance(0.3 + this.drama * 0.1)) outcome = 'line';
      else liner = null;
    }

    // target on the goal plane (frame z, height)
    const kz = keeper ? keeper.st.pos.z * dir : 0;
    let tz = 0, ty = 0.5;
    const side = this.rng.chance(0.5) ? 1 : -1;
    switch (outcome) {
      case 'goal': case 'line': {
        const s = Math.abs(kz) > 0.5 ? -Math.sign(kz) : side;
        tz = s * this.rng.range(1.3, 3.2); ty = this.rng.chance(0.5) ? this.rng.range(0.15, 0.6) : this.rng.range(0.8, 2.1);
        if (kind === 'header') ty = this.rng.range(0.2, 1.8);
        break;
      }
      case 'catch': tz = clamp(kz + this.rng.range(-0.8, 0.8), -3.2, 3.2); ty = this.rng.range(0.3, 1.6); break;
      case 'parry': tz = clamp(kz + side * this.rng.range(0.8, 2.3), -3.3, 3.3); ty = this.rng.range(0.25, 2.0); break;
      case 'tip': tz = side * this.rng.range(2.5, 3.45); ty = this.rng.range(1.3, 2.25); break;
      case 'near':
        if (this.rng.chance(0.65)) { tz = side * (GHW + this.rng.range(0.25, 1.4)); ty = this.rng.range(0.2, 2.2); }
        else { tz = this.rng.range(-3, 3); ty = GH + this.rng.range(0.2, 0.9); }
        break;
      case 'wide':
        if (this.rng.chance(0.6)) { tz = side * (GHW + this.rng.range(1.6, 8)); ty = this.rng.range(0.2, 3); }
        else { tz = this.rng.range(-5, 5); ty = GH + this.rng.range(1.2, 5); }
        break;
      // woodwork is physical: aim at the frame and let the collision decide (in off the post happens)
      case 'post': tz = side * (GHW + this.rng.range(-0.02, 0.15)); ty = this.rng.range(0.3, 2.1); break;
      case 'bar': tz = this.rng.range(-3, 3); ty = GH + this.rng.range(-0.02, 0.15); break;
      case 'block': tz = this.rng.range(-3, 3); ty = this.rng.range(0.2, 1.0); break;
    }
    const wz = tz * dir;
    const speed = kind === 'pen' ? this.rng.range(21, 26)
      : kind === 'header' ? this.rng.range(11, 15)
        : clamp(17 + p.shooting * 8 + (dist > 20 ? 2 : 0) + this.rng.gaussian(0, 1.5), 14, 31);
    const sx = this.bp.x, sz = this.bp.z;
    const D = Math.hypot(goalX - sx, wz - sz);
    const Tt = D / speed;
    const y0 = Math.max(this.bp.y, BR);

    const f = this.releaseBall(p, 'shot', null);
    this.bv = { x: (goalX - sx) / Tt, y: (ty - y0 + 0.5 * G * Tt * Tt) / Tt, z: (wz - sz) / Tt };
    this.bp.y = y0;
    // keeper intercept plane
    let kx = keeper ? keeper.st.pos.x : goalX;
    if (keeper) {
      const kfx = kx * dir, sfx = sx * dir;
      if (kfx < sfx + 0.6) { kx = (sfx + 0.6) * dir; }
      const u = clamp((kx - sx) / (goalX - sx), 0, 1);
      const kzc = sz + (wz - sz) * u;
      // for goals the keeper gets close but not there
      let kzTarget = kzc;
      if (outcome === 'goal' || outcome === 'line') kzTarget = kind === 'pen' && this.rng.chance(0.55) ? -kzc * 0.8 : kzc - Math.sign(kzc - keeper.st.pos.z || 1) * this.rng.range(0.7, 1.4);
      if (outcome === 'near' || outcome === 'wide' || outcome === 'post' || outcome === 'bar') kzTarget = keeper.st.pos.z + (kzc - keeper.st.pos.z) * 0.6;
      f.shot = { shooter: p, kind, outcome, keeper, blocker: outcome === 'block' ? blocker : null, liner, goalX, kx, kz: clamp(kzTarget, -4.5, 4.5), ty, xg, dist, resolved: false };
    } else {
      f.shot = { shooter: p, kind, outcome, keeper: null, blocker: outcome === 'block' ? blocker : null, liner, goalX, kx: goalX, kz: 0, ty, xg, dist, resolved: false };
    }
    this.shotDists.push(dist);
    p.shots++; T.shots++; T.xg += xg;
    this.lastChanceT = this.t;
    const onTarget = outcome === 'goal' || outcome === 'line' || outcome === 'catch' || outcome === 'parry' || outcome === 'tip';
    if (onTarget) T.onTarget++;
    const yards = Math.round(dist * 1.094);
    const key: Line = kind === 'pen' ? 'shot_pen' : kind === 'fk' ? 'shot_fk' : kind === 'header' ? 'shot_header' : kind === 'volley' ? 'shot_volley' : dist > 20 ? 'shot_far' : 'shot_near';
    this.emit(onTarget ? 'shot_on_target' : 'shot', {
      player: p, second: keeper, importance: onTarget ? 0.56 : 0.5 + (dist < 14 ? 0.04 : 0),
      text: this.say(key, { p: surname(p), d: yards }),
    });
    this.lockAnim(p, kind === 'header' ? 'header' : 'kick', 0.4);
    this.counterUntil = Math.max(this.counterUntil, -1);
  }

  /** Called each tick while a shot is in flight. */
  private updateShot(): void {
    const f = this.flight;
    if (!f || !f.shot || f.shot.resolved) return;
    const s = f.shot;
    const dir = s.shooter.team.dir;
    // safety net: a plan that can no longer play out becomes a loose ball
    if (this.t - f.t0 > 3.5 || (this.t - f.t0 > 0.3 && Math.hypot(this.bv.x, this.bv.z) < 3)) {
      s.resolved = true;
      f.kind = 'loose';
      return;
    }
    const k = s.keeper;
    if (k && !k.off) {
      // keeper movement toward the save point
      const bx = this.bp.x * dir, kxf = s.kx * dir;
      const vxf = this.bv.x * dir;
      const tArr = vxf > 0.1 ? (kxf - bx) / vxf : 9;
      k.tx = s.kx; k.tz = s.kz; k.direct = true;
      k.want = tArr < 0.55 ? 9 : 3.5;
      if (tArr < 0.45 && k.lockedAnim !== 'dive' && Math.abs(s.kz - k.st.pos.z) > 0.9) {
        this.lockAnim(k, 'dive', 1.2);
        // ball height as it reaches the keeper decides how high he has to fly
        const yAt = this.bp.y + this.bv.y * tArr - 0.5 * G * tArr * tArr;
        k.diveY = clamp((yAt - 0.75) * 0.55, 0, 0.85);
      }
      if (k.lockedAnim === 'dive' && k.animLock > 0) {
        // dive: move straight to the save point quickly
        const dz = s.kz - k.st.pos.z, dx = s.kx - k.st.pos.x;
        const lim = 6.5 / 30;
        k.st.pos.z += clamp(dz, -lim, lim); k.st.pos.x += clamp(dx, -lim, lim);
      }
    }
    if (s.outcome === 'block' && s.blocker) {
      const b = s.blocker;
      b.tx = this.bp.x; b.tz = this.bp.z; b.want = 6; b.direct = true;
      if (Math.hypot(this.bp.x - b.st.pos.x, this.bp.z - b.st.pos.z) < 1.1 || (this.bp.x - b.st.pos.x) * dir > 0.5) {
        s.resolved = true;
        this.lockAnim(b, 'tackle', 0.6);
        if (this.rng.chance(0.68)) this.deflectBehind(b);
        else {
          const sp = Math.hypot(this.bv.x, this.bv.z) * this.rng.range(0.2, 0.45);
          const a = Math.atan2(-this.bv.z, -this.bv.x) + this.rng.range(-1.3, 1.3);
          this.bv = { x: Math.cos(a) * sp, y: this.rng.range(0.5, 4), z: Math.sin(a) * sp };
          this.lastTouch = b;
          this.flight = { id: ++this.flightSeq, kind: 'loose', from: b, intended: null, t0: this.t, offside: false, shot: null };
          this.scramble = { team: s.shooter.team, until: this.t + 2 };
        }
        const inBox = this.fx(b) < -BOX_X && Math.abs(b.st.pos.z) < BOX_HW;
        if (inBox && s.dist < 20) {
          this.emit('near_miss', { player: s.shooter, second: b, importance: 0.62, pos: { ...b.st.pos }, text: this.say('block', { p: surname(s.shooter), q: surname(b) }) });
          this.afterBigChance(s.shooter.team, 0.5);
        } else {
          this.emit('tackle', { player: b, second: s.shooter, importance: 0.42, replay: s.dist < 18, text: this.say('block', { p: surname(s.shooter), q: surname(b) }) });
        }
      }
      return;
    }
    const bfx = this.bp.x * dir;
    if (s.outcome === 'line' && s.liner) {
      const L = s.liner;
      // throw himself at the point where it will cross the line
      const vxf = this.bv.x * dir;
      const tl = vxf > 0.1 ? Math.max(0, (HL - 0.5 - bfx) / vxf) : 0;
      L.tx = (HL - 0.5) * dir; L.tz = clamp(this.bp.z + this.bv.z * tl, -GHW, GHW); L.want = 9.5; L.direct = true;
      if (bfx >= HL - 1.1) {
        s.resolved = true;
        this.lockAnim(L, Math.abs(this.bp.y) > 1.3 ? 'header' : 'tackle', 0.6);
        this.lastTouch = L;
        if (this.rng.chance(0.6)) this.deflectBehind(L);
        else {
          const sz = Math.sign(this.bp.z) || 1;
          this.bv = { x: -dir * this.rng.range(7, 13), y: this.rng.range(3, 7), z: sz * this.rng.range(2, 8) };
          this.flight = { id: ++this.flightSeq, kind: 'loose', from: L, intended: null, t0: this.t, offside: false, shot: null };
          L.ignoreBall = 0.5;
        }
        this.emit('near_miss', {
          player: s.shooter, second: L, importance: 0.72, replay: true, pos: { ...this.bp },
          text: this.say('line_clear', { p: surname(s.shooter), q: surname(L) }),
        });
        this.afterBigChance(s.shooter.team, 0.9);
      }
      return;
    }
    if ((s.outcome === 'catch' || s.outcome === 'parry' || s.outcome === 'tip') && bfx >= s.kx * dir) {
      s.resolved = true;
      this.resolveSave(s);
      return;
    }
  }

  /** The ball struck a post or the crossbar (physical collision this step). */
  private onWoodwork(kind: 'post' | 'bar'): void {
    const f = this.flight;
    const s = f?.shot && !f.shot.resolved ? f.shot : null;
    const who = s ? s.shooter : this.lastTouch;
    if (s) s.resolved = true;
    if (f) this.flight = { id: ++this.flightSeq, kind: 'loose', from: f.from, intended: null, t0: this.t, offside: false, shot: null };
    if (!who) return;
    const big = !!s || (Math.abs(this.bv.x) + Math.abs(this.bv.y) + Math.abs(this.bv.z) > 6);
    this.emit('woodwork', {
      player: who, importance: s ? 0.78 : 0.6, replay: big, pos: { ...this.bp },
      text: this.say(kind, { p: surname(who) }),
    });
    if (s) { this.afterBigChance(who.team, 1); this.scramble = { team: who.team, until: this.t + 2.5 }; }
  }

  private resolveSave(s: ShotPlan): void {
    const k = s.keeper!;
    const dir = s.shooter.team.dir;
    this.lastTouch = k;
    if (s.outcome === 'catch') {
      this.owner = null;
      this.flight = null;
      this.gain(k, true);
      this.keeperHoldT = 1.5 + this.rng.next() * 1.5;
      this.emit('save', { player: k, second: s.shooter, importance: 0.6, replay: s.xg > 0.15, text: this.say(s.kind === 'pen' ? 'save_pen' : 'save_catch', { k: surname(k) }) });
      if (s.xg > 0.2 || s.kind === 'pen') this.afterBigChance(s.shooter.team, 0.7);
    } else if (s.outcome === 'parry' && this.rng.chance(0.62)) {
      const sp = Math.hypot(this.bv.x, this.bv.z) * this.rng.range(0.25, 0.4);
      this.bv = { x: -dir * sp * this.rng.range(0.5, 1), y: this.rng.range(1, 4), z: this.rng.range(-1, 1) * sp + (this.bp.z >= 0 ? 2 : -2) };
      this.flight = { id: ++this.flightSeq, kind: 'loose', from: k, intended: null, t0: this.t, offside: false, shot: null };
      k.ignoreBall = 0.8;
      this.scramble = { team: s.shooter.team, until: this.t + 2.2 };
      this.emit('save', { player: k, second: s.shooter, importance: 0.72, text: this.say(s.kind === 'pen' ? 'save_pen' : 'save_parry', { k: surname(k) }) });
      this.afterBigChance(s.shooter.team);
    } else {
      // tip round the post / over the bar for a corner
      const sz = Math.sign(this.bp.z) || 1;
      const vx = dir * Math.max(4, Math.abs(this.bv.x) * 0.4);
      const tReach = Math.max(0.12, Math.abs(s.goalX + dir * 0.3 - this.bp.x) / Math.abs(vx));
      if (this.bp.y > 1.5) {
        // over the bar: must be above 2.7m when it reaches the line
        const vy = (2.9 - this.bp.y + 0.5 * G * tReach * tReach) / tReach;
        this.bv = { x: vx, y: Math.max(vy, 3), z: this.bv.z * 0.3 };
      } else {
        const vz = (sz * (GHW + 0.9) - this.bp.z) / tReach;
        this.bv = { x: vx, y: this.rng.range(1, 3), z: vz };
      }
      this.flight = { id: ++this.flightSeq, kind: 'loose', from: k, intended: null, t0: this.t, offside: false, shot: null };
      k.ignoreBall = 2;
      this.emit('save', { player: k, second: s.shooter, importance: 0.75, text: this.say(s.kind === 'pen' ? 'save_pen' : 'save_corner', { k: surname(k) }) });
      this.afterBigChance(s.shooter.team);
    }
  }

  private afterBigChance(team: TeamRt, pMgr = 0.85): void {
    // one reaction pair per chance (rebound scrambles can chain several "chances")
    if (this.scheduled.some((x) => x.tag === 'chance')) return;
    this.schedule(1.0, () => this.emit('crowd_moment', { team: team.side, pos: { x: team.dir * 60, y: 8, z: 0 }, importance: 0.32, text: this.say('crowd_chance', { team: team.prof.name }) }), 'chance');
    if (this.rng.chance(pMgr)) {
      this.schedule(2.4, () => this.emit('manager_reaction', {
        team: team.side, pos: { x: team.dugoutX, y: 0, z: BENCH_Z }, importance: 0.4,
        text: this.say('manager_chance', { m: team.prof.manager, team: team.prof.name }),
      }), 'chance');
    }
  }

  // -------------------------------------------------------------------------
  // Possession changes
  // -------------------------------------------------------------------------

  private gain(p: SP, hands: boolean): void {
    const f = this.flight;
    if (f && f.offside && f.from.team === p.team && p === f.intended && this.phase === 'open_play') {
      this.callOffside(p);
      return;
    }
    if (f && f.from !== p && f.kind !== 'shot' && f.kind !== 'loose' && f.kind !== 'clear') {
      if (f.from.team === p.team) {
        p.team.passesOk++;
        if (f.kind === 'through' && this.fx(p) > 10 && !f.offside) {
          this.emit('pass', {
            player: f.from, second: p, importance: this.fx(p) > 30 ? 0.25 : 0.16, pos: { ...p.st.pos },
            text: this.say('through_ball', { p: surname(f.from), q: surname(p) }),
          });
        }
      }
      else if (f.kind === 'pass' || f.kind === 'through' || f.kind === 'long' || f.kind === 'cross' || f.kind === 'cutback') {
        if (!hands) this.emit('interception', { player: p, second: f.from, text: this.say('interception', { q: surname(p) }) });
        else this.emit('interception', { player: p, importance: 0.18, text: this.say('keeper_claim', { k: surname(p) }) });
      }
    }
    const prevSide = this.lastTouch ? this.lastTouch.team.side : this.possSide;
    this.owner = p; p.st.hasBall = true; this.flight = null; this.lastTouch = p;
    this.bv = { x: 0, y: 0, z: 0 };
    p.touches++;
    p.holding = false;
    p.carryT = 0;
    const { d } = this.nearestOpp(p);
    p.decideT = d < 3 ? 0.08 + this.rng.next() * 0.12 : 0.15 + this.rng.next() * 0.2;
    if (this.fx(p) > 20) p.decideT = Math.min(p.decideT, 0.12 + this.rng.next() * 0.15);
    // dribble default: forward
    p.dribX = p.team.dir; p.dribZ = 0;
    if (prevSide !== p.team.side || this.possSide !== p.team.side) {
      this.turnoverT = this.t; this.turnoverFx = this.fx(p); this.counterEmitted = this.turnoverFx > 15;
      this.counterUntil = -1;
    }
    this.possSide = p.team.side;
    if (hands) { this.keeperHoldT = 1.4 + this.rng.next() * 1.4; return; }
    // late challenge on the receiver
    if (this.phase === 'open_play' && !p.isGK) {
      const { o, d: od } = this.nearestOpp(p);
      const inBox = this.fx(p) > BOX_X && Math.abs(p.st.pos.z) < BOX_HW;
      if (o && !o.isGK && od < 1.7 && o.stun <= 0 && !(inBox && this.rng.chance(0.75))) {
        const closing = (o.st.vel.x * (p.st.pos.x - o.st.pos.x) + o.st.vel.z * (p.st.pos.z - o.st.pos.z)) / Math.max(od, 0.1);
        if (closing > 1.0 && this.rng.chance((0.14 + o.aggression * 0.16 + this.drama * 0.06) / this.defK * (this.calmFor(p.team) ? 0.25 : 1))) this.foul(o, p);
      }
    }
  }

  private callOffside(p: SP): void {
    this.emit('offside', { player: p, text: this.say('offside', { p: surname(p) }) });
    this.startRestart({ kind: 'free_kick', team: p.team.other, x: p.st.pos.x, z: p.st.pos.z, direct: false, taker: null });
  }

  private header(p: SP): void {
    const f = this.flight;
    const dir = p.team.dir;
    const pfx = this.fx(p), pfz = this.fz(p);
    this.lockAnim(p, 'header', 0.45);
    const attacking = f && f.from.team === p.team;
    if (attacking && f && (f.kind === 'cross' || f.kind === 'long') && pfx > 37 && Math.abs(pfz) < 16) {
      if (f.offside && f.intended === p) { this.callOffside(p); return; }
      // contest with nearest defender
      const { o, d } = this.nearestOpp(p);
      if (o && d < 1.6 && !o.isGK) {
        // aerial duels get physical
        const rf = this.rng.next();
        if (rf < 0.12 + this.drama * 0.06) { this.lockAnim(o, 'fall', 1.5); this.foul(p, o); return; }
        if (rf < 0.145 + this.drama * 0.06) { this.foul(o, p); return; }
        const pWin = clamp(0.64 + (p.heading - o.heading) * 0.6 + (1 - this.defK) * 0.3, 0.35, 0.86);
        if (!this.rng.chance(pWin)) { this.clearHeader(o); return; }
      }
      this.owner = p; // momentarily, for shoot()
      this.shoot(p, 'header');
      return;
    }
    if (!attacking && pfx < 0) { this.clearHeader(p); return; }
    // otherwise: cushion it down
    this.gain(p, false);
    // (ball eases down to his feet in updateBall — no snap)
    void dir;
  }

  /** Defensive deflection that sends the ball behind for a corner. */
  private deflectBehind(p: SP): void {
    const dir = p.team.dir;
    const sz = Math.sign(this.bp.z) || (this.rng.chance(0.5) ? 1 : -1);
    this.owner = null; p.st.hasBall = false;
    // looping deflection sized to clear the byline
    const vy = this.rng.range(5.5, 8);
    const T = 2 * vy / G;
    const toLine = Math.abs(-dir * (HL + 1.5) - this.bp.x);
    const vx = clamp(toLine / T, 4, 24);
    this.bv = { x: -dir * vx, y: vy, z: sz * this.rng.range(1, 5) };
    this.bp.y = Math.max(this.bp.y, 0.3);
    this.lastTouch = p; p.ignoreBall = 0.6;
    this.flight = { id: ++this.flightSeq, kind: 'loose', from: p, intended: null, t0: this.t, offside: false, shot: null };
  }

  private clearHeader(p: SP): void {
    const dir = p.team.dir;
    this.lockAnim(p, 'header', 0.45);
    if (this.fx(p) < -BOX_X && this.rng.chance(0.5)) { this.deflectBehind(p); return; }
    const tfx = clamp(this.fx(p) + this.rng.range(12, 25), -40, 30);
    const tfz = clamp(this.fz(p) + this.rng.range(-15, 15), -30, 30);
    this.owner = p;
    this.loftedPass(p, tfx * dir, tfz * dir, BR, 1.2 + this.rng.next() * 0.6, 'clear', null);
    if (this.fx(p) < -HL + 18) this.emit('interception', { player: p, importance: 0.15, text: this.say('header_clear', { q: surname(p) }) });
  }

  // -------------------------------------------------------------------------
  // Fouls & restarts
  // -------------------------------------------------------------------------

  private foul(def: SP, victim: SP): void {
    const T = def.team;
    T.fouls++;
    const vfx = this.fx(victim), vfz = this.fz(victim);
    const pen = vfx > BOX_X && Math.abs(vfz) < BOX_HW;
    this.lockAnim(victim, 'fall', 1.7); victim.stun = 1.8;
    this.lockAnim(def, 'tackle', 0.7);
    this.owner = null; victim.st.hasBall = false; this.flight = null;
    this.bv = { x: victim.st.vel.x * 0.5, y: 0, z: victim.st.vel.z * 0.5 };

    let card: 'yellow' | 'red' | null = null;
    const pY = 0.13 + this.drama * 0.12 + def.aggression * 0.1 + (this.counterUntil > this.t ? 0.3 : 0) + (pen ? 0.15 : 0) + (vfx > 25 ? 0.05 : 0) + (this.cynical ? 0.25 : 0);
    this.cynical = false;
    const pR = 0.002 + this.drama * 0.005 + (this.counterUntil > this.t && vfx > 20 ? 0.025 : 0);
    if (!def.isGK && this.rng.chance(pR)) card = 'red';
    else if (this.rng.chance(def.yellow > 0 ? pY * 0.35 : pY)) card = 'yellow';
    const fev = this.emit('foul', {
      player: def, second: victim, pos: { ...victim.st.pos }, replay: card !== null || pen,
      importance: pen ? 0.5 : card ? 0.4 : 0.3,
      text: this.say(pen ? 'foul_box' : 'foul', { p: surname(def), q: surname(victim) }),
    });
    void fev;
    if (pen) {
      this.emit('penalty', {
        team: victim.team.side, player: victim, pos: { x: (HL - 11) * victim.team.dir, y: 0, z: 0 },
        text: this.say('penalty', { team: victim.team.prof.name, q: surname(victim) }),
      });
    }
    if (card) {
      this.schedule(1.8, () => this.showCard(def, card!));
    }
    const restart: Restart = pen
      ? { kind: 'penalty', team: victim.team, x: (HL - 11) * victim.team.dir, z: 0, direct: true, taker: null }
      : { kind: 'free_kick', team: victim.team, x: victim.st.pos.x, z: victim.st.pos.z, direct: vfx > HL - 34 && Math.abs(vfz) < 22 && vfx < BOX_X + 2, taker: null };
    this.counterUntil = -1;
    // routine fouls are restarted quickly; cards / dangerous free kicks get a longer beat (replay window)
    this.startRestart(restart, card ? 3.2 : pen || restart.direct ? 1.8 : 0.9 + this.rng.next() * 0.4);
  }

  private showCard(p: SP, card: 'yellow' | 'red'): void {
    if (p.off) return;
    const team = p.team;
    if (card === 'yellow') {
      p.yellow++;
      team.yellow++;
      if (p.yellow >= 2) {
        team.red++;
        this.emit('red_card', { player: p, text: this.say('second_yellow', { p: surname(p), team: team.prof.name }) });
        this.sendOff(p);
      } else {
        this.emit('yellow_card', { player: p, text: this.say('yellow', { p: surname(p) }) });
      }
    } else {
      team.red++;
      this.emit('red_card', { player: p, text: this.say('red', { p: surname(p), team: team.prof.name }) });
      this.sendOff(p);
    }
  }

  private sendOff(p: SP): void {
    p.off = true;
    p.st.sentOff = true;
    p.subOff = false;
    if (this.owner === p) this.owner = null;
    p.st.hasBall = false;
    if (this.restart?.taker === p) this.restart.taker = null;
    this.lockAnim(p, 'dejected', 2.5);
  }

  /** Begin a stoppage that leads to the given restart. */
  private startRestart(r: Restart, stopDur = -1): void {
    if (this.owner) { this.owner.st.hasBall = false; this.owner = null; }
    for (const p of this.all) { p.pending = null; p.runT = 0; }
    this.flight = null;
    this.keeperHoldT = 0;
    this.restart = r;
    this.stoppages++;
    this.stoppageGaps.push(this.t - this.lastRestartT);
    this.setPhase('stoppage');
    // routine restarts are taken quickly; anything after a chance keeps a beat for the replay
    const afterChance = this.t - this.lastChanceT < 3;
    this.stopDur = stopDur > 0 ? stopDur : afterChance ? 1.4 + this.rng.next() * 0.6 : 0.7 + this.rng.next() * 0.6;
    const rd = this.rng;
    this.setDur = r.kind === 'throw_in' ? rd.range(0.8, 1.6)
      : r.kind === 'corner' ? rd.range(2.6, 3.8)
        : r.kind === 'goal_kick' ? (afterChance ? rd.range(2, 3) : rd.range(1.3, 2.2))
          : r.kind === 'penalty' ? rd.range(4.5, 6)
            : r.direct ? rd.range(3.2, 4.5) : rd.range(0.9, 2.0);
    this.counterUntil = -1;
    this.trySubs();
    // event for the restart
    const tn = r.team.prof.name;
    switch (r.kind) {
      case 'corner': r.team.corners++; this.emit('corner', { team: r.team.side, pos: { x: r.x, y: 0, z: r.z }, text: this.say('corner', { team: tn }) }); break;
      case 'throw_in': this.emit('throw_in', { team: r.team.side, pos: { x: r.x, y: 0, z: r.z }, text: this.say('throw_in', { team: tn }) }); break;
      case 'goal_kick': break;
      case 'free_kick':
        this.emit('free_kick', {
          team: r.team.side, pos: { x: r.x, y: 0, z: r.z }, importance: r.direct ? 0.45 : 0.18,
          text: this.say(r.direct ? 'free_kick_danger' : 'free_kick', { team: tn }),
        });
        break;
      default: break;
    }
  }

  /** Queue planned substitutions at a stoppage: the departing player walks to the halfway touchline. */
  private trySubs(): void {
    for (const team of [this.home, this.away]) {
      if (!team.subPlan.length || this.clock < team.subPlan[0]! || !team.benchLeft.length) continue;
      if (this.subQueue.some((q) => q.team === team)) continue;
      // pick tired outfield player
      const cands = team.players.filter((p) => !p.off && !p.isGK && !p.subOff && p !== this.restart?.taker);
      if (!cands.length) continue;
      cands.sort((a, b) => a.stamina - b.stamina || a.i - b.i);
      const out = cands[Math.min(cands.length - 1, this.rng.int(0, 2))]!;
      let bi = team.benchLeft.findIndex((b) => b.position === out.prof.position);
      if (bi < 0) bi = team.benchLeft.findIndex((b) => b.position !== 'GK');
      if (bi < 0) continue;
      team.subPlan.shift();
      const inProf = team.benchLeft.splice(bi, 1)[0]!;
      out.subOff = true;
      this.subQueue.push({ team, out, inProf, t0: this.t });
    }
  }

  /** Walk departing players off; swap them for the substitute at the halfway touchline. */
  private updateSubs(): void {
    if (!this.subQueue.length) return;
    const keep: typeof this.subQueue = [];
    for (const q of this.subQueue) {
      const { team, out } = q;
      if (out.off) continue; // sent off meanwhile
      const lane = team.side === 'home' ? -1 : 1;
      const ex = lane * 1.6, ez = -HW - 0.9;
      out.tx = ex; out.tz = ez; out.direct = true;
      const d = Math.hypot(out.st.pos.x - ex, out.st.pos.z - ez);
      out.want = d > 25 ? 6.2 : 4.8;
      if (d > 0.6 && this.t - q.t0 < 16) { keep.push(q); continue; }
      // the swap: substitute steps on from the board right beside him
      const np = this.makePlayer(q.inProf, team, out.slot);
      np.i = out.i;
      np.st.pos = { x: lane * 0.5, y: 0, z: -HW - 0.9 };
      np.tx = np.st.pos.x; np.tz = np.st.pos.z + 3;
      np.st.heading = Math.PI / 2; // facing +z (onto the pitch)
      team.players[team.players.indexOf(out)] = np;
      this.all[out.i] = np;
      out.subOff = false;
      if (this.restart?.taker === out) this.restart.taker = null;
      this.emit('substitution', {
        player: np, second: out, pos: { ...np.st.pos },
        text: this.say('substitution', { team: team.prof.name, p: surname(np), q: surname(out) }),
      });
    }
    this.subQueue = keep;
  }

  private updateStoppage(): void {
    const r = this.restart;
    if (!r) { this.setPhase('open_play'); return; }
    this.setPieceTargets(r, 3.8);
    this.updateSubs();
    if (this.phaseT >= this.stopDur && !this.subQueue.length) {
      this.setPhase('set_piece');
      this.startBallMove(r.x, r.z, Math.min(1.4, this.setDur * 0.6));
    }
  }

  private updateSetPiece(): void {
    const r = this.restart;
    if (!r) { this.setPhase('open_play'); return; }
    this.setPieceTargets(r, 6);
    const taker = r.taker;
    if (!taker || taker.off) { r.taker = this.pickTaker(r); return; }
    const ready = Math.hypot(taker.st.pos.x - taker.tx, taker.st.pos.z - taker.tz) < 1.0;
    if ((this.phaseT >= this.setDur && ready && !this.ballMove) || this.phaseT > this.setDur + 4) this.executeRestart(r, taker);
  }

  private startBallMove(x: number, z: number, dur: number): void {
    const dist = Math.hypot(x - this.bp.x, z - this.bp.z);
    this.ballMove = { fx: this.bp.x, fz: this.bp.z, fy: this.bp.y, tx: x, tz: z, t0: this.t, dur: Math.max(0.3, dur, Math.min(3, dist / 22)) };
    this.inNet = false;
  }

  private pickTaker(r: Restart): SP {
    const act = this.active(r.team);
    const gk = this.gk(r.team);
    if (r.kind === 'goal_kick' && gk) return gk;
    let best = act.find((p) => !p.isGK && !p.subOff) ?? act[0]!, bs = -1e9;
    for (const p of act) {
      if (p.isGK || p.stun > 0.5 || p.subOff) continue;
      let s: number;
      const d = Math.hypot(p.st.pos.x - r.x, p.st.pos.z - r.z);
      switch (r.kind) {
        case 'throw_in': s = -d + (p.slot.role === 'FB' || p.slot.role === 'WB' ? 6 : 0); break;
        case 'corner': s = p.passing * 10 - d * 0.05 + (p.slot.role === 'W' || p.slot.role === 'WM' ? 2 : 0); break;
        case 'penalty': s = p.shooting * 10 + (p.prof.position === 'FWD' ? 2 : 0); break;
        case 'free_kick': s = r.direct ? p.shooting * 6 + p.passing * 4 : -d; break;
        default: s = -d;
      }
      if (s > bs) { bs = s; best = p; }
    }
    return best;
  }

  /** Assign targets for all players for a restart. */
  private setPieceTargets(r: Restart, speed: number): void {
    if (!r.taker || r.taker.off) r.taker = this.pickTaker(r);
    const A = r.team, D = A.other, dir = A.dir;
    const sfx = r.x * dir, sfz = r.z * dir; // spot in A frame
    const put = (p: SP, fx: number, fz: number, want = speed) => {
      p.tx = fx * dir; p.tz = fz * dir; p.want = want; p.direct = true;
    };
    for (const p of this.all) if (p.off) this.offTarget(p);
    const taker = r.taker;
    const aPlayers = this.active(A).filter((p) => p !== taker);
    const dPlayers = this.active(D);

    if (r.kind === 'corner' || (r.kind === 'free_kick' && sfx > 18 && !r.direct)) {
      const corner = r.kind === 'corner';
      const s = Math.sign(sfz) || 1;
      if (taker) put(taker, sfx + (corner ? 0.6 : -1.2), sfz + (corner ? s * 0.6 : 0));
      const lineX = corner ? 47 : clamp(sfx + 11, 40, 46);
      const boxSlots: [number, number][] = corner
        ? [[46.5, -3.5], [45, 2.5], [48.5, 0.5], [43, -6.5], [44, 6.5], [37, -4], [37, 5]]
        : [[lineX - 1, -4], [lineX - 1, 3], [lineX - 1.5, -8], [lineX - 0.8, 7.5], [lineX - 2, 0], [lineX - 9, -5], [lineX - 9, 6]];
      // attackers: best headers forward, keep 2 back
      const sorted = [...aPlayers].filter((p) => !p.isGK).sort((a, b) => (b.heading + (b.prof.position === 'FWD' ? 0.3 : 0)) - (a.heading + (a.prof.position === 'FWD' ? 0.3 : 0)) || a.i - b.i);
      const marks: [number, number][] = [];
      sorted.forEach((p, i) => {
        if (i < boxSlots.length && i < sorted.length - 2) {
          const [x, z] = boxSlots[i]!;
          const jz = ((p.i * 37) % 10) / 10 - 0.5;
          put(p, x, z * s + jz);
          marks.push([x, z * s + jz]);
        } else put(p, i === sorted.length - 1 ? -5 : 8, i % 2 ? 12 : -12);
      });
      const agk = this.gk(A); if (agk) put(agk, -42, 0, 4);
      // defenders
      const dgk = this.gk(D);
      if (dgk) put(dgk, corner ? 51.6 : 50.5, s * 0.8, 5);
      const dOut = dPlayers.filter((p) => !p.isGK).sort((a, b) => (a.slot.x - b.slot.x) || a.i - b.i);
      dOut.forEach((p, i) => {
        if (i < marks.length) { const [x, z] = marks[i]!; put(p, x + 1.0, z - s * 0.4); }
        else if (i === dOut.length - 1) put(p, corner ? 20 : 15, 0);
        else if (corner && i === marks.length) put(p, 51, s * 3.4);
        else put(p, corner ? 44 : lineX + 0.5, ((i * 5) % 14) - 7);
      });
      if (!corner) this.pushFromSpot(D, r, 9.15, dir);
      return;
    }
    if (r.kind === 'penalty') {
      if (taker) put(taker, HL - 12.4, 0);
      const zs = [-17, -13, -9.5, 9.5, 13, 17, -5, 5, -2, 2];
      const all = [...aPlayers.filter((p) => !p.isGK), ...dPlayers.filter((p) => !p.isGK)].sort((a, b) => a.i - b.i);
      // interleave
      all.forEach((p, i) => {
        const z = zs[i % zs.length]! + (i >= zs.length ? 1.2 : 0);
        const x = Math.abs(z) < 7 ? 31.8 : 35.2;
        put(p, x, z);
      });
      const agk = this.gk(A); if (agk) put(agk, -44, 0, 3);
      const dgk = this.gk(D); if (dgk) put(dgk, HL - 0.2, 0, 3);
      return;
    }
    if (r.kind === 'free_kick' && r.direct) {
      if (taker) {
        const gx = HL - sfx, gz = -sfz, gl = Math.hypot(gx, gz) || 1;
        put(taker, sfx - (gx / gl) * 1.6, sfz - (gz / gl) * 1.6);
      }
      // wall
      const gx = HL - sfx, gz = -sfz, gl = Math.hypot(gx, gz) || 1;
      const ux = gx / gl, uz = gz / gl;
      const wx = sfx + ux * 9.15, wz = sfz + uz * 9.15;
      const nWall = gl < 22 ? 4 : 3;
      const dOut = dPlayers.filter((p) => !p.isGK).sort((a, b) => (a.slot.x - b.slot.x) || a.i - b.i);
      const near = Math.sign(sfz) || 1;
      dOut.forEach((p, i) => {
        if (i < nWall) {
          const off = (i - (nWall - 1) / 2) * 0.62 + near * 0.6;
          put(p, wx - uz * off, wz + ux * off);
        } else if (i === dOut.length - 1) put(p, 10, 0);
        else put(p, 45 - (i % 2) * 3, ((i * 4) % 16) - 8);
      });
      const dgk = this.gk(D); if (dgk) put(dgk, HL - 0.6, -near * 0.9, 4);
      const out = aPlayers.filter((p) => !p.isGK);
      out.forEach((p, i) => {
        if (i < 5) put(p, Math.min(44, sfx + 7) - (i % 2), ((i * 6) % 20) - 10);
        else put(p, i % 2 ? 0 : 12, i % 2 ? 10 : -10);
      });
      const agk = this.gk(A); if (agk) put(agk, -42, 0, 4);
      return;
    }
    if (r.kind === 'kickoff') return;

    // generic: throw-in, goal kick, deep free kick → open-play shape around the spot
    for (const team of [A, D]) {
      const inPoss = team === A;
      const tdir = team.dir;
      const bfx = r.x * tdir, bfz = r.z * tdir;
      for (const p of team.players) {
        if (p.off || p === taker) continue;
        if (p.isGK) {
          if (r.kind === 'goal_kick' && team === A) continue;
          this.keeperTarget(p, inPoss, bfx, bfz); p.want = 4; continue;
        }
        const s = p.slot;
        let bc: number, depth: number, wm: number, shift: number;
        if (inPoss) { bc = clamp(bfx * 0.62 + 6, -18, 24); depth = 1.25; wm = 1.05; shift = 0.15; }
        else { bc = clamp(bfx * 0.62 - 9, -31, 12); depth = 0.8; wm = 0.66; shift = 0.38; }
        let x = bc + s.x * depth, z = s.z * wm + bfz * shift;
        if (inPoss && r.kind !== 'goal_kick') x = Math.min(x, Math.max(this.offLine[team.side] - 0.7, 0));
        if (r.kind === 'goal_kick' && !inPoss) x = Math.max(x, -BOX_X + 1 - 0);
        x = clamp(x, -48, 48); z = clamp(z, -32, 32);
        p.tx = x * tdir; p.tz = z * tdir; p.want = speed * 0.8; p.direct = true;
      }
    }
    if (r.kind === 'throw_in') {
      // two nearby options
      const close = aPlayers.filter((p) => !p.isGK).sort((a, b) => Math.hypot(a.tx - r.x, a.tz - r.z) - Math.hypot(b.tx - r.x, b.tz - r.z) || a.i - b.i).slice(0, 2);
      close.forEach((p, i) => { put(p, sfx + (i ? -7 : 8), sfz * 0.72 + (i ? 2 : -2)); });
      if (taker) put(taker, sfx, sfz + Math.sign(sfz) * 0.4);
    } else if (r.kind === 'goal_kick') {
      // goal kick: opponents must be outside the box
      for (const p of dPlayers) {
        const fxA = p.tx * dir; // A frame
        if (fxA < -BOX_X + 1 && Math.abs(p.tz) < BOX_HW + 1) p.tx = (-BOX_X + 2) * dir;
      }
      if (taker) put(taker, sfx - 1.5, sfz);
    } else {
      if (taker) put(taker, sfx - 1.2, sfz);
      this.pushFromSpot(D, r, 9.15, dir);
    }
  }

  private pushFromSpot(D: TeamRt, r: Restart, rad: number, _dir: number): void {
    for (const p of D.players) {
      if (p.off) continue;
      const dx = p.tx - r.x, dz = p.tz - r.z;
      const d = Math.hypot(dx, dz);
      if (d < rad) {
        // push toward own goal side
        const ox = d > 0.1 ? dx / d : -D.dir, oz = d > 0.1 ? dz / d : 0;
        p.tx = r.x + ox * rad; p.tz = r.z + oz * rad;
      }
    }
  }

  private executeRestart(r: Restart, taker: SP): void {
    this.setPhase('open_play');
    this.lastRestartT = this.t;
    this.bp = { x: r.x, y: BR, z: r.z }; this.bv = { x: 0, y: 0, z: 0 }; this.ballMove = null; this.inNet = false;
    this.owner = taker; taker.st.hasBall = true; this.lastTouch = taker; this.possSide = r.team.side;
    this.flight = null;
    this.ballPinned = true;
    const A = r.team, dir = A.dir;
    const mates = this.active(A).filter((p) => p !== taker && !p.isGK);
    const pickBox = (): SP | null => {
      let best: SP | null = null, bs = -1e9;
      for (const q of mates) {
        const fx = this.fx(q), fz = this.fz(q);
        if (fx < 36 || Math.abs(fz) > 14) continue;
        const s = q.heading * 2 + this.oppDistAt(A, q.st.pos.x, q.st.pos.z) + this.rng.next() * 1.5;
        if (s > bs) { bs = s; best = q; }
      }
      return best;
    };
    const nearest = (minD: number, maxD: number): SP | null => {
      let best: SP | null = null, bs = -1e9;
      for (const q of mates) {
        const d = Math.hypot(q.st.pos.x - r.x, q.st.pos.z - r.z);
        if (d < minD || d > maxD) continue;
        const s = this.oppDistAt(A, q.st.pos.x, q.st.pos.z) - d * 0.1 + this.rng.next() * 2 + this.fx(q) * 0.02;
        if (s > bs) { bs = s; best = q; }
      }
      return best;
    };
    switch (r.kind) {
      case 'throw_in': {
        const q = nearest(4, 20);
        taker.pending = { kind: 'throw', to: q, x: 0, z: 0 }; taker.windup = 0.35;
        this.ballPinned = false;
        this.lockAnim(taker, 'throw', 0.7);
        break;
      }
      case 'corner': {
        const q = pickBox();
        if (q && this.rng.chance(0.9)) { taker.pending = { kind: 'cross', to: q, x: 0, z: 0 }; taker.windup = 0.3; }
        else { const n = nearest(5, 20); if (n) { taker.pending = { kind: 'pass', to: n, x: 0, z: 0 }; taker.windup = 0.2; } else taker.decideT = 0.2; }
        if (taker.pending) this.lockAnim(taker, 'kick', 0.55);
        break;
      }
      case 'goal_kick': {
        const short = nearest(8, 25);
        if (short && this.rng.chance(0.35)) taker.pending = { kind: 'pass', to: short, x: 0, z: 0 };
        else taker.pending = { kind: 'goal_kick', to: null, x: 0, z: 0 };
        taker.windup = 0.35; this.lockAnim(taker, 'kick', 0.6);
        this.emit('goal_kick', { team: A.side, player: taker, text: this.say('goal_kick', { team: A.prof.name }) });
        break;
      }
      case 'penalty': {
        taker.pending = { kind: 'shot', to: null, x: 0, z: 0, shotKind: 'pen' }; taker.windup = 0.45;
        this.lockAnim(taker, 'kick', 0.7);
        break;
      }
      case 'free_kick': {
        const sfx = r.x * dir;
        if (r.direct && this.rng.chance(0.82)) {
          taker.pending = { kind: 'shot', to: null, x: 0, z: 0, shotKind: 'fk' }; taker.windup = 0.4;
        } else if (sfx > 18) {
          const q = pickBox();
          if (q) taker.pending = { kind: 'cross', to: q, x: 0, z: 0 };
          taker.windup = 0.35;
        } else {
          const q = nearest(6, 28);
          if (q) taker.pending = { kind: sfx < -20 && this.rng.chance(0.4) ? 'long' : 'pass', to: q, x: 0, z: 0 };
          taker.windup = 0.25;
        }
        if (taker.pending) this.lockAnim(taker, 'kick', taker.windup + 0.25);
        else taker.decideT = 0.2;
        break;
      }
      case 'kickoff': this.takeKickoff(); return;
    }
    this.restart = null;
    this.maybeCalm(A, r.x * dir);
  }

  /** Sometimes a team takes the sting out of the game and builds patiently from the back. */
  private maybeCalm(team: TeamRt, fx: number): void {
    if (fx > 5 || this.counterUntil > this.t) return;
    if (this.rng.chance(0.08 + (1 - this.drama) * 0.08)) this.calm = { team, until: this.t + this.rng.range(28, 44) };
  }

  // -------------------------------------------------------------------------
  // Goals & celebrations
  // -------------------------------------------------------------------------

  private onGoal(goalEndX: number): void {
    const scoringTeam = goalEndX * this.home.dir > 0 ? this.home : this.away;
    const shot = this.flight?.shot ?? null;
    let scorer = this.lastTouch ?? shot?.shooter ?? this.active(scoringTeam)[10] ?? this.active(scoringTeam)[0]!;
    const own = scorer.team !== scoringTeam;
    if (scoringTeam.side === 'home') this.score.home++; else this.score.away++;
    this.scheduled = this.scheduled.filter((x) => x.tag !== 'chance');
    this.inNet = true;
    this.flight = null; this.owner = null;
    let assister: SP | null = null;
    if (!own && this.lastPasser && this.lastPasser.p.team === scoringTeam && this.lastPasser.p !== scorer && this.t - this.lastPasser.t < 8) assister = this.lastPasser.p;
    const conceding = scoringTeam.other;
    const k = this.gk(conceding);
    const dist = shot ? shot.dist : 10;
    let key: Line = 'goal';
    if (own) key = 'own_goal';
    else if (shot?.kind === 'pen') key = 'goal_pen';
    else if (shot?.kind === 'fk') key = 'goal_fk';
    else if (shot?.kind === 'header') key = 'goal_header';
    else if (dist > 22) key = 'goal_long';
    const sText = `${this.score.home}-${this.score.away}`;
    this.stoppages++;
    this.stoppageGaps.push(this.t - this.lastRestartT);
    this.emit('goal', {
      team: scoringTeam.side, player: scorer, second: assister,
      text: this.say(key, { p: surname(scorer), team: scoringTeam.prof.name, k: k ? surname(k) : 'the keeper', s: sText, d: Math.round(dist * 1.094) }),
    });
    // credit celebration to a scoring-team player for own goals
    const celebrant = own ? (shot?.shooter && shot.shooter.team === scoringTeam ? shot.shooter : this.kickoffTaker(scoringTeam)) : scorer;
    // scorer heads for the nearest corner flag at the end he attacked; team-mates get huddle slots by distance
    const cz = (Math.sign(celebrant.st.pos.z) || 1) * (HW - 2.2);
    const cx = scoringTeam.dir * (HL - 2.2);
    const mates = this.active(scoringTeam).filter((q) => q !== celebrant && !q.isGK)
      .sort((a, b) => Math.hypot(a.st.pos.x - cx, a.st.pos.z - cz) - Math.hypot(b.st.pos.x - cx, b.st.pos.z - cz) || a.i - b.i);
    const slots = new Map<SP, number>();
    mates.forEach((q, i) => slots.set(q, i));
    this.celeb = { scorer: celebrant, team: scoringTeam, cx, cz, slots };
    this.setPhase('goal_celebration');
    this.kickoffTeam = conceding;
    for (const p of this.all) { p.pending = null; p.runT = 0; p.st.hasBall = false; }
    this.schedule(1.4, () => this.emit('celebration', { player: celebrant, text: this.say('celebration', { p: surname(celebrant) }) }));
    this.schedule(2.8, () => this.emit('crowd_moment', { team: scoringTeam.side, pos: { x: goalEndX * 1.1, y: 8, z: 0 }, importance: 0.4, text: this.say('crowd_goal', { team: scoringTeam.prof.name }) }));
    this.schedule(4.6, () => this.emit('manager_reaction', {
      team: scoringTeam.side, pos: { x: scoringTeam.dugoutX, y: 0, z: BENCH_Z }, importance: 0.5,
      text: this.say('manager_goal', { m: scoringTeam.prof.manager, team: scoringTeam.prof.name }),
    }));
    if (this.rng.chance(0.5)) {
      this.schedule(6.8, () => this.emit('manager_reaction', {
        team: conceding.side, pos: { x: conceding.dugoutX, y: 0, z: BENCH_Z }, importance: 0.35,
        text: this.say('manager_concede', { m: conceding.prof.manager, team: conceding.prof.name }),
      }));
    }
  }

  private updateCelebration(): void {
    const c = this.celeb;
    if (!c) { this.setPhase('kickoff'); return; }
    const sc = c.scorer;
    const { cx, cz } = c;
    const T = this.phaseT;
    // scorer sprints to the corner flag / crowd
    sc.tx = cx; sc.tz = cz; sc.want = 7.8; sc.direct = true;
    const atCorner = Math.hypot(sc.st.pos.x - cx, sc.st.pos.z - cz) < 1.5;
    if ((atCorner || T > 4.8) && sc.animLock <= 0 && T < 7) this.lockAnim(sc, 'celebrate', 2.4);
    // huddle: rings of slots on the infield side of the scorer, spaced so bodies don't merge
    const inA = Math.atan2(-cz, -cx);
    for (const p of c.team.players) {
      if (p.off || p === sc) continue;
      if (p.isGK) {
        p.tx = p.st.pos.x; p.tz = p.st.pos.z; p.want = 0;
        if (p.animLock <= 0 && T < 6) this.lockAnim(p, 'celebrate', 2);
        continue;
      }
      if (T > 6.5) continue;
      const k = c.slots.get(p) ?? 0;
      const ring = k < 4 ? 0 : 1;
      const j = ring === 0 ? k : k - 4;
      const n = ring === 0 ? 4 : 6;
      const spread = ring === 0 ? 2.2 : 2.6;
      const a = inA + (n > 1 ? (j / (n - 1) - 0.5) * spread : 0);
      const r = ring === 0 ? 1.5 : 2.75;
      p.tx = sc.st.pos.x + Math.cos(a) * r; p.tz = sc.st.pos.z + Math.sin(a) * r;
      const d = Math.hypot(p.tx - p.st.pos.x, p.tz - p.st.pos.z);
      p.want = d > 6 ? 7.2 : d > 1.5 ? 4 : 1.5; p.direct = true;
      if (d < 1.2 && p.animLock <= 0 && T > 1.5) this.lockAnim(p, 'celebrate', 1.6 + (k % 3) * 0.3);
    }
    // opponents: hands on heads, keeper slumps to the turf, then they trudge back
    for (const p of c.team.other.players) {
      if (p.off) continue;
      if (T < 0.05) {
        if (p.isGK) this.lockAnim(p, 'fall', 1.6);
        else this.lockAnim(p, 'dejected', 4.2 + (p.i % 4) * 0.6);
      }
      if (p.isGK && p.lockedAnim === 'fall' && p.animLock <= 0 && T < 3) this.lockAnim(p, 'dejected', 3.0);
      if (p.animLock > 0) { p.tx = p.st.pos.x; p.tz = p.st.pos.z; p.want = 0; continue; }
      const [x, z] = this.kickoffSpot(p, true);
      p.tx = x * p.team.dir; p.tz = z * p.team.dir; p.want = 3.6; p.direct = true;
    }
    // celebrating team drifts back once the huddle breaks up
    if (T > 6.5) {
      for (const p of c.team.players) {
        if (p.off || p.animLock > 0) continue;
        const [x, z] = this.kickoffSpot(p, false);
        p.tx = x * p.team.dir; p.tz = z * p.team.dir; p.want = 5.5; p.direct = true;
      }
    }
    if (T > 8.5) {
      this.celeb = null;
      this.setPhase('kickoff');
      this.placeForKickoff(false);
      this.startBallMove(0, 0, 1.6);
    }
  }

  /**
   * Half-time: everyone walks off toward the tunnel (near side, halfway). After HT_WALK seconds the
   * teams swap ends in place and the kickoff phase walks them back out (no teleports).
   */
  private updateHalfTime(): void {
    for (const p of this.all) {
      if (p.off) { this.offTarget(p); continue; }
      // funnel into the tunnel mouth, home queue on the left, away on the right
      const lane = p.team.side === 'home' ? -1 : 1;
      const k = p.team.players.indexOf(p);
      p.tx = lane * (1.2 + (k % 3) * 1.0); p.tz = TUNNEL_Z + 1.2 - Math.floor(k / 3) * 0.9;
      p.want = 5.2; p.direct = true;
      if (p.animLock > 0 && p.lockedAnim !== 'dejected' && p.lockedAnim !== 'celebrate') p.animLock = 0;
    }
    if (this.phaseT > HT_WALK) {
      // second half: swap ends
      this.half = 2;
      this.home.dir = -1; this.away.dir = 1;
      this.halfStartT = -1;
      this.clockFrozen = false;
      this.addedSet = false;
      this.clock = CLOCK_HALF;
      this.kickoffTeam = this.kickoffFirst.other;
      this.placeForKickoff(false);
      this.owner = null; this.flight = null; this.lastTouch = null; this.inNet = false; this.ballMove = null;
      for (const p of this.all) { p.animLock = 0; p.stun = 0; p.pending = null; p.runT = 0; p.stamina = Math.min(1, p.stamina + 0.15); }
      this.lastRestartT = this.t;
      this.setPhase('kickoff');
    }
  }

  // -------------------------------------------------------------------------
  // Movement
  // -------------------------------------------------------------------------

  private movePlayers(dt: number): void {
    const n = this.all.length;
    const celebrating = this.phase === 'goal_celebration';
    for (const p of this.all) { p.sepX = 0; p.sepZ = 0; }
    for (let i = 0; i < n; i++) {
      const a = this.all[i]!;
      if (a.off) continue;
      for (let j = i + 1; j < n; j++) {
        const b = this.all[j]!;
        if (b.off) continue;
        const dx = b.st.pos.x - a.st.pos.x, dz = b.st.pos.z - a.st.pos.z;
        const same = a.team === b.team;
        const R = same ? (celebrating ? 1.05 : 2.4) : (a === this.owner || b === this.owner ? 0.75 : 1.1);
        if (Math.abs(dx) > R || Math.abs(dz) > R) continue;
        const d = Math.hypot(dx, dz);
        if (d >= R) continue;
        const push = (R - d) * (same ? (celebrating ? 7 : 1.6) : 2.5);
        const ux = d > 1e-4 ? dx / d : ((i * 7 + j) % 2 ? 1 : -1), uz = d > 1e-4 ? dz / d : 0;
        a.sepX -= ux * push; a.sepZ -= uz * push;
        b.sepX += ux * push; b.sepZ += uz * push;
      }
    }
    const bx = this.bp.x, bz = this.bp.z;
    for (const p of this.all) {
      const st = p.st;
      const dx = p.tx - st.pos.x, dz = p.tz - st.pos.z;
      const d = Math.hypot(dx, dz);
      const fatigue = 0.88 + 0.12 * p.stamina;
      // TEMPO: the whole game runs a touch quicker than real football (more action per broadcast minute)
      const tempo = this.phase === 'open_play' || this.phase === 'goal_celebration' ? TEMPO : 1;
      const maxV = Math.min(p.want * tempo, Math.min((7.0 + p.pace * 1.9) * tempo, 9.6)) * fatigue;
      let vdes = d < 0.3 ? 0 : Math.min(maxV, d * 1.5);
      if (p.stun > 0 || (p.animLock > 0 && (p.lockedAnim === 'fall' || p.lockedAnim === 'dive' || p.lockedAnim === 'throw' || p.lockedAnim === 'dejected' || p.lockedAnim === 'celebrate'))) vdes = 0;
      let dvx = d > 1e-4 ? (dx / d) * vdes : 0, dvz = d > 1e-4 ? (dz / d) * vdes : 0;
      if (!(p.lockedAnim === 'dive' && p.animLock > 0)) { dvx += p.sepX; dvz += p.sepZ; }
      const ax = dvx - st.vel.x, az = dvz - st.vel.z;
      const al = Math.hypot(ax, az);
      const curV = Math.hypot(st.vel.x, st.vel.z);
      const accel = (vdes < curV ? 9 : 5.5) * dt;
      if (al > accel) { st.vel.x += (ax / al) * accel; st.vel.z += (az / al) * accel; }
      else { st.vel.x = dvx; st.vel.z = dvz; }
      st.pos.x += st.vel.x * dt; st.pos.z += st.vel.z * dt;
      st.pos.x = clamp(st.pos.x, -HL - 4.5, HL + 4.5);
      st.pos.z = clamp(st.pos.z, -HW - 6, HW + 4);
      const sp = Math.hypot(st.vel.x, st.vel.z);
      // heading
      let th: number;
      if (sp > 0.7) th = Math.atan2(st.vel.z, st.vel.x);
      else th = Math.atan2(bz - st.pos.z, bx - st.pos.x);
      const dh = wrapAngle(th - st.heading);
      const rate = (sp > 0.7 ? 8 : 4) * dt;
      st.heading = wrapAngle(st.heading + clamp(dh, -rate, rate));
      // anim
      if (p.animLock > 0) {
        st.anim = p.lockedAnim;
        st.animPhase = clamp(1 - p.animLock / p.animLen, 0, 1);
        // diving keeper's root rises and falls in an arc (airborne for the first ~70% of the dive)
        st.pos.y = p.lockedAnim === 'dive' ? p.diveY * Math.sin(Math.PI * Math.min(1, st.animPhase / 0.7)) : 0;
        if (p.lockedAnim === 'celebrate' || p.lockedAnim === 'dejected') st.animPhase = (st.animPhase * p.animLen / 1.2) % 1;
      } else {
        st.pos.y = 0;
        const prevA = st.anim;
        const h = (lo: number, hi: number, cur: PlayerAnim, low: PlayerAnim) => cur === low ? sp < hi : sp < lo; // hysteresis band
        let a: PlayerAnim;
        if (h(0.3, 0.7, prevA, 'idle')) a = 'idle';
        else if (h(3.9, 4.6, prevA, 'jog')) a = 'jog';
        else if (h(6.3, 6.9, prevA, 'run')) a = 'run';
        else a = 'sprint';
        st.anim = a;
        const freq = a === 'idle' ? 0.5 : 1.0 + sp * 0.2;
        st.animPhase = (st.animPhase + dt * freq) % 1;
      }
      // stats
      if (this.phase !== 'half_time' && this.phase !== 'full_time') {
        p.dist += sp * dt;
        if (sp > p.topSpeed) p.topSpeed = sp;
        p.stamina = clamp(p.stamina - (sp > 6.5 ? 0.0009 : sp > 4 ? 0.0003 : -0.0002) * dt * 30 * 0.12, 0.2, 1);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Ball
  // -------------------------------------------------------------------------

  private updateBall(dt: number): void {
    if (this.ballMove) {
      const m = this.ballMove;
      const u = clamp((this.t - m.t0) / m.dur, 0, 1);
      this.bp.x = m.fx + (m.tx - m.fx) * u;
      this.bp.z = m.fz + (m.tz - m.fz) * u;
      this.bp.y = BR + (m.fy - BR) * (1 - u) + Math.sin(Math.PI * u) * Math.min(4, Math.hypot(m.tx - m.fx, m.tz - m.fz) * 0.15);
      this.bv = { x: 0, y: 0, z: 0 };
      this.spin = 0;
      if (u >= 1) { this.ballMove = null; this.bp.y = BR; }
      return;
    }
    const o = this.owner;
    if (o && this.ballPinned && o.pending) {
      this.bv = { x: 0, y: 0, z: 0 }; this.spin = 0;
      return;
    }
    this.ballPinned = false;
    if (o) {
      const h = o.st.heading;
      const prevX = this.bp.x, prevY = this.bp.y, prevZ = this.bp.z;
      const fx = Math.cos(h), fz = Math.sin(h);
      if (o.isGK && this.keeperHoldT > 0) {
        this.bp = { x: o.st.pos.x + fx * 0.3, y: 1.05, z: o.st.pos.z + fz * 0.3 };
      } else if (o.lockedAnim === 'throw' && o.animLock > 0) {
        this.bp = { x: o.st.pos.x + fx * 0.1, y: 2.15, z: o.st.pos.z + fz * 0.1 };
      } else {
        const sp = Math.hypot(o.st.vel.x, o.st.vel.z);
        const off = 0.45 + Math.min(sp / 8, 1) * 0.35 * (0.5 + 0.5 * Math.sin(o.st.animPhase * Math.PI * 2));
        this.bp = { x: o.st.pos.x + fx * off, y: BR, z: o.st.pos.z + fz * off };
      }
      // ease into the new position after a change of owner instead of snapping
      const jx = this.bp.x - prevX, jy = this.bp.y - prevY, jz = this.bp.z - prevZ;
      const jump = Math.hypot(jx, jy, jz);
      const maxStep = 14 * dt;
      if (jump > maxStep) {
        const k = maxStep / jump;
        this.bp = { x: prevX + jx * k, y: prevY + jy * k, z: prevZ + jz * k };
      }
      this.bv = { x: o.st.vel.x, y: 0, z: o.st.vel.z };
      this.spin = Math.hypot(o.st.vel.x, o.st.vel.z) / BR;
      return;
    }
    // free ball physics (with substeps for accuracy)
    this.prevBall.x = this.bp.x; this.prevBall.y = this.bp.y; this.prevBall.z = this.bp.z;
    // near the goal frame use fine substeps so a 30 m/s shot can't tunnel through a post
    const nearFrame = !this.inNet && Math.abs(Math.abs(this.bp.x) - HL) < 4 && Math.abs(this.bp.z) < GHW + 3;
    const sub = nearFrame ? 10 : 2;
    const h = dt / sub;
    if (this.woodCd > 0) this.woodCd -= dt;
    this.woodHit = null;
    for (let s = 0; s < sub; s++) {
      const b = this.bp, v = this.bv;
      if (b.y > BR + 0.001 || v.y > 0.01) {
        v.y -= G * h;
        b.x += v.x * h; b.y += v.y * h; b.z += v.z * h;
        if (b.y < BR) {
          b.y = BR;
          if (v.y < -1.5) { v.y = -v.y * 0.5; v.x *= 0.85; v.z *= 0.85; } else v.y = 0;
        }
      } else {
        const sp = Math.hypot(v.x, v.z);
        if (sp > 0) { const ns = Math.max(0, sp - this.rollDecel * h); v.x *= ns / sp; v.z *= ns / sp; }
        b.x += v.x * h; b.z += v.z * h;
      }
      if (nearFrame) this.collideFrame();
    }
    this.spin = Math.hypot(this.bv.x, this.bv.z) / BR;

    if (this.inNet) { this.constrainNet(); return; }
    if (this.woodHit && this.phase === 'open_play' && this.woodCd <= 0) { this.woodCd = 0.4; this.onWoodwork(this.woodHit); }
    if (this.phase !== 'open_play') {
      // dead ball: keep it near the pitch
      this.bp.x = clamp(this.bp.x, -HL - 5, HL + 5); this.bp.z = clamp(this.bp.z, -HW - 5, HW + 5);
      if (Math.abs(this.bp.x) >= HL + 5 || Math.abs(this.bp.z) >= HW + 5) { this.bv.x *= 0.5; this.bv.z *= 0.5; }
      return;
    }
    this.updateShot();
    if (this.phase !== 'open_play' || this.owner) return;
    if (this.checkOut()) return;
    this.controlCheck();
  }

  /** Ball vs posts (vertical cylinders) and crossbar (horizontal cylinder) at both ends. */
  private collideFrame(): void {
    const b = this.bp, v = this.bv;
    const ex = (Math.sign(b.x) || 1) * (HL + POST_R); // frame centre line (posts sit on the goal line)
    const R = BR + POST_R;
    const bounce = (nx: number, ny: number, nz: number, d: number): boolean => {
      const vn = v.x * nx + v.y * ny + v.z * nz;
      // push out of the frame
      const pen = R - d;
      b.x += nx * pen; b.y += ny * pen; b.z += nz * pen;
      if (vn >= 0) return false;
      const e = 0.62;
      v.x -= (1 + e) * vn * nx; v.y -= (1 + e) * vn * ny; v.z -= (1 + e) * vn * nz;
      // tangential friction
      v.x *= 0.9; v.y *= 0.9; v.z *= 0.9;
      return -vn > 2.5;
    };
    // posts
    if (b.y < GH + POST_R) {
      for (const pz of [-GHW - POST_R, GHW + POST_R]) {
        const dx = b.x - ex, dz = b.z - pz;
        const d = Math.hypot(dx, dz);
        if (d < R && d > 1e-6) { if (bounce(dx / d, 0, dz / d, d)) this.woodHit = 'post'; return; }
      }
    }
    // crossbar
    if (Math.abs(b.z) < GHW + POST_R) {
      const dx = b.x - ex, dy = b.y - (GH + POST_R);
      const d = Math.hypot(dx, dy);
      if (d < R && d > 1e-6) { if (bounce(dx / d, dy / d, 0, d)) this.woodHit = 'bar'; }
    }
  }

  private constrainNet(): void {
    const b = this.bp, v = this.bv;
    const sx = Math.sign(b.x) || 1;
    if (Math.abs(b.x) > HL + 1.7) { b.x = sx * (HL + 1.7); v.x = -v.x * 0.15; v.z *= 0.4; v.y *= 0.4; }
    if (Math.abs(b.z) > GHW - 0.15) { b.z = Math.sign(b.z) * (GHW - 0.15); v.z = -v.z * 0.2; }
    if (b.y > GH - 0.15) { b.y = GH - 0.15; v.y = -Math.abs(v.y) * 0.2; }
    if (Math.abs(b.x) < HL + 0.15) { b.x = sx * (HL + 0.15); v.x = sx * Math.abs(v.x) * 0.2; }
  }

  /** Returns true if the ball left play (or a goal was scored). */
  private checkOut(): boolean {
    const b = this.bp;
    if (Math.abs(b.x) > HL + BR) {
      const endX = Math.sign(b.x) * HL;
      // where exactly did it cross the line? (fast shots move ~0.7m per frame)
      const pb = this.prevBall;
      const ax = Math.abs(pb.x), bx = Math.abs(b.x);
      const u = bx > ax ? clamp((HL + BR - ax) / (bx - ax), 0, 1) : 1;
      const cz = pb.z + (b.z - pb.z) * u, cy = pb.y + (b.y - pb.y) * u;
      if (Math.abs(cz) < GHW - BR && cy < GH - BR) {
        this.onGoal(endX);
        return true;
      }
      const f = this.flight;
      const shot = f?.shot;
      if (shot && !shot.resolved && shot.outcome === 'near') {
        this.emit('near_miss', { player: shot.shooter, pos: { ...b }, text: this.say(shot.kind === 'pen' ? 'pen_miss' : shot.kind === 'header' ? 'near_header' : Math.abs(b.z) < GHW ? 'near_bar' : 'near_post', { p: surname(shot.shooter) }) });
        this.afterBigChance(shot.shooter.team);
      } else if (shot && !shot.resolved && shot.outcome === 'wide' && shot.kind === 'pen') {
        this.emit('near_miss', { player: shot.shooter, importance: 0.7, text: this.say('pen_miss', { p: surname(shot.shooter) }) });
      }
      // defending team of this end: team whose dir points away from it
      const defending = this.home.dir * endX < 0 ? this.home : this.away;
      const lt = this.lastTouch;
      if (lt && lt.team === defending) {
        const sz = Math.sign(b.z) || 1;
        this.startRestart({ kind: 'corner', team: defending.other, x: Math.sign(endX) * (HL - 0.3), z: sz * (HW - 0.3), direct: false, taker: null });
      } else {
        const gkx = Math.sign(endX) * (HL - 5.5);
        const gz = b.z > 0 ? 2.5 : -2.5;
        const textFor = shot && !shot.resolved && shot.outcome === 'wide' && shot.kind !== 'pen'
          ? this.say('wide', { p: surname(shot.shooter) }) : null;
        this.startRestart({ kind: 'goal_kick', team: defending, x: gkx, z: gz, direct: false, taker: null }, textFor ? 1.8 : -1);
        if (textFor) this.emit('goal_kick', { team: defending.side, importance: 0.12, pos: { x: gkx, y: 0, z: gz }, text: textFor });
      }
      return true;
    }
    if (Math.abs(b.z) > HW + BR) {
      const lt = this.lastTouch;
      const team = lt ? lt.team.other : (this.possSide === 'home' ? this.away : this.home);
      this.startRestart({ kind: 'throw_in', team, x: clamp(b.x, -HL + 1, HL - 1), z: Math.sign(b.z) * HW, direct: false, taker: null });
      return true;
    }
    return false;
  }

  private controlCheck(): void {
    const f = this.flight;
    if (f?.shot && !f.shot.resolved) return;
    const b = this.bp;
    const ballSpeed = Math.hypot(this.bv.x, this.bv.z);
    // a pass that has died is just a loose ball now
    if (f && f.kind !== 'loose' && !f.shot && ballSpeed < 2.5 && b.y < 0.3 && this.t - f.t0 > 0.5) { f.kind = 'loose'; f.intended = null; }
    let best: SP | null = null, bd = 1e9, bestHands = false;
    for (const p of this.all) {
      if (p.off || p.stun > 0 || p.ignoreBall > 0) continue;
      if (p.animLock > 0 && (p.lockedAnim === 'fall' || p.lockedAnim === 'dejected')) continue;
      const d = Math.hypot(b.x - p.st.pos.x, b.z - p.st.pos.z);
      if (d > 1.6) continue;
      const pfx = this.fx(p);
      const comesForCross = !(f && f.kind === 'cross' && f.from.team !== p.team) || f.id % 5 === 0;
      const hands = p.isGK && pfx < -HL + 16.5 && Math.abs(p.st.pos.z) < BOX_HW && b.y < 2.7 && d < (comesForCross ? 1.5 : 0.9);
      // attackers attacking a delivery into the box get a half-yard (game feel: more chances)
      const attackingBall = (!!f && f.from.team === p.team && (f.kind === 'cross' || f.kind === 'cutback' || f.kind === 'through') && pfx > 30) ||
        (!!this.scramble && this.scramble.team === p.team && this.t < this.scramble.until && pfx > 30);
      const reach = (b.y < 1.0 ? (p.isGK ? 1.05 : 0.9) : b.y < 2.5 ? 0.8 : 0) + (attackingBall ? 0.25 : 0);
      if (!hands && d > reach) continue;
      if (f && f.id === p.attempted && ballSpeed > 4) continue;
      const de = d - (attackingBall ? 0.45 : 0);
      if (de < bd) { bd = de; best = p; bestHands = hands; }
    }
    if (!best) return;
    const p = best;
    // opponents intercepting a moving pass may miss it
    const speed = Math.hypot(this.bv.x, this.bv.z);
    if (f && f.from.team !== p.team && f.kind !== 'loose' && !bestHands) {
      p.attempted = f.id;
      const midfield = Math.abs(b.x) < 30;
      const pc = clamp(1.0 - speed * 0.045, 0.3, 0.95) * (0.55 + 0.35 * p.tackling) * this.defK * (midfield ? 0.7 : 0.6);
      if (!this.rng.chance(pc)) {
        // deflection
        if (this.rng.chance(0.4)) {
          this.bv.x *= 0.6; this.bv.z = this.bv.z * 0.6 + this.rng.range(-3, 3);
          this.lastTouch = p;
          if (f) f.kind = 'loose';
        }
        return;
      }
    }
    if (!bestHands && f && f.from.team !== p.team && (f.kind === 'cross' || f.kind === 'cutback' || f.kind === 'through' || f.kind === 'pass' || f.kind === 'long') &&
      (this.fx(p) < -BOX_X + 4 || (this.fx(p) < -40 && Math.abs(p.st.pos.z) > 20)) && this.rng.chance(f.kind === 'pass' || f.kind === 'long' ? 0.4 : 0.62)) {
      this.lockAnim(p, 'tackle', 0.5);
      this.deflectBehind(p);
      return;
    }
    // keeper under pressure from a cross: punches it behind rather than catching
    if (bestHands && f && f.from.team !== p.team && (f.kind === 'cross' || f.kind === 'cutback') && b.y > 1.2 && this.rng.chance(0.35)) {
      this.lockAnim(p, 'header', 0.45);
      this.emit('interception', { player: p, importance: 0.2, text: this.say('keeper_claim', { k: surname(p) }) });
      this.deflectBehind(p);
      return;
    }
    if (bestHands) { this.gain(p, f ? f.from.team !== p.team : false); if (!(f && f.from.team !== p.team)) this.keeperHoldT = 0; return; }
    if (b.y >= 1.0) { this.header(p); return; }
    // first-time finish in the box
    const ownPass = f && f.from.team === p.team && (f.kind === 'cutback' || f.kind === 'cross' || f.kind === 'pass' || f.kind === 'through') && !f.offside;
    const looseInBox = (!f || f.kind === 'loose' || f.kind === 'clear') && this.lastTouch !== p;
    if (f && (ownPass || looseInBox)) {
      const pfx = this.fx(p), pfz = this.fz(p);
      const inZone = pfx > HL - (looseInBox ? 18 : 16) && Math.abs(pfz) < (looseInBox ? 13 : 11);
      // a cleared ball dropping to an attacker on the edge of the box: hit it first time
      const edge = looseInBox && f.from.team !== p.team && !inZone && Math.hypot(HL - pfx, pfz) < 22 && Math.abs(pfz) < 12;
      if ((inZone && this.rng.chance((looseInBox ? 0.65 : 0.6) + this.drama * 0.35)) || (edge && this.rng.chance(0.3 + this.drama * 0.2))) {
        this.owner = p; p.st.hasBall = true; this.flight = null; p.touches++; this.lastTouch = p;
        if (f.from.team === p.team) p.team.passesOk++;
        this.lastPasser = { p: f.from, t: this.t };
        this.shoot(p, b.y > 0.4 ? 'volley' : 'open');
        return;
      }
    }
    this.gain(p, false);
    // ball eases to his feet in updateBall
  }

  // -------------------------------------------------------------------------
  // Officials, intensity, snapshot
  // -------------------------------------------------------------------------

  private updateOfficials(dt: number): void {
    const b = this.bp;
    let rx: number, rz: number;
    if (this.phase === 'half_time') { rx = 0; rz = -30; }
    else if (this.phase === 'pre_match') { rx = 0; rz = -6; }
    else if (this.phase === 'goal_celebration' || this.phase === 'kickoff') { rx = 3; rz = -8; }
    else {
      const diag = clamp(b.x * 0.4, -20, 20);
      rx = b.x - Math.sign(b.x || 1) * 7;
      rz = clamp(diag * 0.7 + b.z * 0.4, -26, 26);
      const dx = rx - b.x, dz = rz - b.z, d = Math.hypot(dx, dz);
      if (d < 10) { const k = 10 / Math.max(d, 0.1); rx = b.x + dx * k; rz = b.z + dz * k; }
      rx = clamp(rx, -HL + 3, HL - 3); rz = clamp(rz, -HW + 2, HW - 2);
    }
    const k = damp(1.5, dt);
    const tvx = clamp((rx - this.ref.x) * 1.2, -7, 7), tvz = clamp((rz - this.ref.z) * 1.2, -7, 7);
    this.refV.x += (tvx - this.refV.x) * k * 2; this.refV.z += (tvz - this.refV.z) * k * 2;
    this.ref.x += this.refV.x * dt; this.ref.z += this.refV.z * dt;
    // assistants track the second-last defender
    const secondLast = (team: TeamRt): number => {
      // world x of second-last defender of `team`'s own goal
      const xs = this.active(team).map((p) => p.st.pos.x * -team.dir).sort((a, c) => c - a);
      return (xs[1] ?? 0) * -team.dir;
    };
    const defPos = this.home.dir < 0 ? this.home : this.away; // defends +x end
    const defNeg = defPos.other;
    let a1 = Math.max(secondLast(defPos), b.x); a1 = clamp(a1, 0, HL);
    let a2 = Math.min(secondLast(defNeg), b.x); a2 = clamp(a2, -HL, 0);
    if (this.phase === 'half_time') { a1 = 2; a2 = -2; }
    const ak = damp(3, dt);
    this.ar1.x += clamp((a1 - this.ar1.x) * ak, -7 * dt, 7 * dt);
    this.ar2.x += clamp((a2 - this.ar2.x) * ak, -7 * dt, 7 * dt);
  }

  private updateIntensity(dt: number): void {
    let target = 0.1;
    let fx = this.bp.x, fy = this.bp.y, fz = this.bp.z;
    switch (this.phase) {
      case 'open_play': {
        const team = this.owner ? this.owner.team : this.lastTouch ? this.lastTouch.team : null;
        if (team) {
          const bfx = this.bp.x * team.dir, bfz = this.bp.z * team.dir;
          const tx = clamp(bfx / HL, 0, 1);
          target = 0.22 + 0.45 * Math.pow(tx, 1.4);
          if (bfx > BOX_X && Math.abs(bfz) < BOX_HW) target += 0.22;
          if (this.counterUntil > this.t) target += 0.25;
        } else target = 0.3;
        if (this.flight?.shot && !this.flight.shot.resolved) target = 1;
        break;
      }
      case 'stoppage': target = 0.12; break;
      case 'set_piece': {
        const r = this.restart;
        target = r?.kind === 'penalty' ? 0.85 : r?.kind === 'corner' ? 0.55 : r?.kind === 'free_kick' && r.direct ? 0.6 : 0.15;
        if (r) { fx = r.x; fz = r.z; fy = 0; }
        break;
      }
      case 'goal_celebration':
        target = 0.45;
        if (this.celeb) { fx = this.celeb.scorer.st.pos.x; fz = this.celeb.scorer.st.pos.z; fy = 1; }
        break;
      case 'kickoff': target = 0.2; fx = 0; fz = 0; fy = 0; break;
      case 'half_time': case 'full_time': target = 0.05; fx = 0; fz = 0; fy = 0; break;
      default: target = 0.1;
    }
    if (target >= 1) this.intensity = 1;
    else this.intensity += (target - this.intensity) * damp(this.phase === 'open_play' ? 2.5 : 3, dt);
    this.focus = { x: fx, y: fy, z: fz };
  }

  snapshot(): WorldSnapshot {
    return {
      t: this.t,
      matchClock: this.clock,
      half: this.half,
      phase: this.phase,
      score: { ...this.score },
      possession: this.phase === 'open_play' ? (this.owner ? this.owner.team.side : this.lastTouch ? this.lastTouch.team.side : null) : this.possSide,
      ball: { pos: { ...this.bp }, vel: { ...this.bv }, spin: this.spin, ownerId: this.owner ? this.owner.prof.id : null },
      players: this.all.map((p) => ({
        ...p.st, pos: { ...p.st.pos }, vel: { ...p.st.vel }, hasBall: p === this.owner,
      })),
      officials: { referee: { ...this.ref }, assistant1: { ...this.ar1 }, assistant2: { ...this.ar2 } },
      focus: { ...this.focus },
      intensity: this.intensity,
    };
  }

  stats(): MatchStats {
    const total = this.home.possT + this.away.possT || 1;
    const ts = (t: TeamRt): TeamMatchStats => ({
      possessionPct: Math.round((t.possT / total) * 1000) / 10,
      shots: t.shots, shotsOnTarget: t.onTarget, corners: t.corners, fouls: t.fouls, yellow: t.yellow, red: t.red,
      passes: t.passes, passAccuracyPct: t.passes ? Math.round((t.passesOk / t.passes) * 1000) / 10 : 0,
      xg: Math.round(t.xg * 100) / 100,
    });
    const players: MatchStats['players'] = {};
    for (const [id, p] of this.everPlayed) {
      if (!p.touches && !p.dist) continue;
      players[id] = {
        touches: p.touches, passes: p.passes, shots: p.shots,
        distanceKm: Math.round(p.dist / 10) / 100, topSpeedKmh: Math.round(p.topSpeed * 36) / 10,
      };
    }
    const scorers = this._events.filter((e) => e.type === 'goal').map((e) => ({ playerId: e.playerId!, team: e.team!, matchClock: e.matchClock }));
    return { home: ts(this.home), away: ts(this.away), scorers, players };
  }

  /** Extra diagnostics for the headless runner/tests (not part of the contract). */
  diagnostics(): SimDiagnostics {
    const g = this.stoppageGaps;
    return { stoppages: this.stoppages, avgGap: g.length ? g.reduce((a, b) => a + b, 0) / g.length : 0, gaps: [...g], shotDists: [...this.shotDists] };
  }
}

export interface SimDiagnostics {
  stoppages: number;
  /** Mean real seconds of open play between stoppages. */
  avgGap: number;
  gaps: number[];
  /** Distance to goal (m) of every shot taken. */
  shotDists: number[];
}

export type FootballSim = MatchSim & {
  /** Extra numbers for the headless runner / tests (not part of the MatchSim contract). */
  diagnostics(): SimDiagnostics;
};

export function createFootballSim(config: MatchConfig): FootballSim {
  return new FootballSimImpl(config);
}
